import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Space,
  Progress,
  Statistic,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
  notification,
} from "antd";
import {
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  CopyOutlined,
  DownOutlined,
  FileTextOutlined,
  KeyOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MenuOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import ApiKeyServices from "../../services/api-key.services";
import ApiLogService from "../../services/api-log.services";
import ApiServices from "../../services/api.services";
import AuthService from "../../services/auth.services";
import ProfileServices from "../../services/profile.services";
import { setAuth, setUser } from "../../redux/commonSlice";
import SwipeableDrawer from "./SwipeableDrawer";
import "./portal.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const BRAND_CHART = {
  primary: "#00a4bc",
  secondary: "#4995ad",
  deep: "#016282",
  success: "#1f9d55",
  danger: "#eb6363",
  muted: "#b8d4df",
};

const trafficLineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 14,
        padding: 14,
        usePointStyle: true,
        pointStyle: "line",
        color: BRAND_CHART.deep,
        font: { size: 12, weight: "600" },
      },
    },
  },
  scales: {
    x: {
      ticks: { color: "#58778b", maxRotation: 45, minRotation: 0, font: { size: 11 } },
      grid: { color: "rgba(1, 98, 130, 0.06)" },
    },
    y: {
      ticks: { color: "#58778b", precision: 0, font: { size: 11 } },
      grid: { color: "rgba(1, 98, 130, 0.06)" },
      beginAtZero: true,
    },
  },
};

const buildTrafficLineDatasets = ({ total, success, errors }) => [
  {
    label: "Total calls",
    data: total,
    borderColor: BRAND_CHART.primary,
    backgroundColor: "rgba(0, 164, 188, 0.08)",
    borderWidth: 2.5,
    pointRadius: 3,
    pointBackgroundColor: BRAND_CHART.primary,
    tension: 0.35,
    fill: false,
  },
  {
    label: "Successful",
    data: success,
    borderColor: BRAND_CHART.success,
    backgroundColor: "rgba(31, 157, 85, 0.08)",
    borderWidth: 2,
    pointRadius: 3,
    pointBackgroundColor: BRAND_CHART.success,
    tension: 0.35,
    fill: false,
  },
  {
    label: "Errors",
    data: errors,
    borderColor: BRAND_CHART.danger,
    backgroundColor: "rgba(235, 99, 99, 0.08)",
    borderWidth: 2,
    pointRadius: 3,
    pointBackgroundColor: BRAND_CHART.danger,
    tension: 0.35,
    fill: false,
  },
];

const compactChartLegend = {
  position: "bottom",
  labels: {
    boxWidth: 8,
    padding: 5,
    font: { size: 11, weight: "500" },
    color: BRAND_CHART.deep,
  },
};

const compactDoughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: compactChartLegend },
  cutout: "68%",
};

const compactBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: { legend: { display: false } },
  scales: {
    x: {
      ticks: { color: BRAND_CHART.deep, font: { size: 11 }, precision: 0 },
      grid: { color: "rgba(1, 98, 130, 0.06)" },
    },
    y: {
      ticks: { color: BRAND_CHART.deep, font: { size: 11 } },
      grid: { display: false },
    },
  },
};

const ADMIN_USAGE_SORT_OPTIONS = [
  { value: "requests_desc", label: "Most requests" },
  { value: "requests_asc", label: "Fewest requests" },
  { value: "recent_desc", label: "Most recent API use" },
  { value: "recent_asc", label: "Oldest API use" },
  { value: "created_desc", label: "Account created: Newest → Oldest" },
  { value: "created_asc", label: "Account created: Oldest → Newest" },
  { value: "quota_left_asc", label: "Quota left: Low → High" },
  { value: "quota_left_desc", label: "Quota left: High → Low" },
];

const ADMIN_USAGE_DEFAULT_VIEW_STORAGE_KEY = "admin_usage_default_view_v1";
const ADMIN_ACTION_TIMELINE_STORAGE_KEY = "admin_action_timeline_v1";

const mergeAdminUsageWithQuota = (rows, adminQuotaSummary) => {
  const quotaByUser = adminQuotaSummary.reduce((acc, row) => {
    acc[String(row.user_id)] = row;
    return acc;
  }, {});

  return rows.map((item) => {
    const quota = quotaByUser[String(item.user_id)] || {};
    const quotaTotal = Number(quota.quota_total || 0);
    const quotaUsed = Number(quota.quota_used || 0);
    const quotaRemaining = Number(quota.quota_remaining || 0);
    const quotaPercent = quotaTotal > 0 ? Math.min(Math.round((quotaUsed / quotaTotal) * 100), 100) : 0;
    const quotaRemainingPercent =
      quotaTotal > 0 ? Math.min(Math.round((quotaRemaining / quotaTotal) * 100), 100) : 100;

    return {
      ...item,
      key: item.user_id,
      plan_type: quota.plan_type || item.plan_type || "free",
      quota_used: quotaUsed,
      quota_total: quotaTotal,
      quota_remaining: quotaRemaining,
      quota_percent: quotaPercent,
      quota_remaining_percent: quotaRemainingPercent,
      cycle_id: quota.cycle_id || null,
      quota_reset_policy: quota.quota_reset_policy || null,
    };
  });
};

const sortAdminMergedUserRows = (rows, sortKey) => {
  const list = [...rows];
  switch (sortKey) {
    case "requests_asc":
      return list.sort((a, b) => Number(a.total_requests || 0) - Number(b.total_requests || 0));
    case "recent_desc":
      return list.sort((a, b) => {
        const ta = a.last_request_at ? new Date(a.last_request_at).getTime() : 0;
        const tb = b.last_request_at ? new Date(b.last_request_at).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return Number(b.total_requests || 0) - Number(a.total_requests || 0);
      });
    case "recent_asc":
      return list.sort((a, b) => {
        const na = a.last_request_at ? new Date(a.last_request_at).getTime() : Number.POSITIVE_INFINITY;
        const nb = b.last_request_at ? new Date(b.last_request_at).getTime() : Number.POSITIVE_INFINITY;
        if (na !== nb) return na - nb;
        return Number(a.total_requests || 0) - Number(b.total_requests || 0);
      });
    case "created_desc":
      return list.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return String(a.username || "").localeCompare(String(b.username || ""));
      });
    case "created_asc":
      return list.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.created_at ? new Date(b.created_at).getTime() : Number.POSITIVE_INFINITY;
        if (ta !== tb) return ta - tb;
        return String(a.username || "").localeCompare(String(b.username || ""));
      });
    case "quota_left_asc":
      return list.sort((a, b) => {
        const ra = Number(a.quota_remaining_percent ?? 100);
        const rb = Number(b.quota_remaining_percent ?? 100);
        if (ra !== rb) return ra - rb;
        return Number(b.quota_percent || 0) - Number(a.quota_percent || 0);
      });
    case "quota_left_desc":
      return list.sort((a, b) => {
        const ra = Number(a.quota_remaining_percent ?? 100);
        const rb = Number(b.quota_remaining_percent ?? 100);
        if (rb !== ra) return rb - ra;
        return Number(a.quota_percent || 0) - Number(b.quota_percent || 0);
      });
    case "requests_desc":
    default:
      return list.sort((a, b) => Number(b.total_requests || 0) - Number(a.total_requests || 0));
  }
};

const ADMIN_QUOTA_SORT_OPTIONS = [
  { value: "plan_premium_first", label: "Plan: Premium → Free" },
  { value: "usage_desc", label: "Usage: High → Low" },
  { value: "remaining_desc", label: "Remaining: High → Low" },
];

const getQuotaUsedPercent = (record) => {
  const total = Number(record?.quota_total || 0);
  const used = Number(record?.quota_used || 0);
  return total > 0 ? Math.round((used / total) * 100) : 0;
};

const sortAdminQuotaRows = (rows, sortKey) => {
  const list = [...rows];
  switch (sortKey) {
    case "usage_desc":
      return list.sort((a, b) => {
        const diff = getQuotaUsedPercent(b) - getQuotaUsedPercent(a);
        if (diff !== 0) return diff;
        return Number(b.quota_used || 0) - Number(a.quota_used || 0);
      });
    case "remaining_desc":
      return list.sort((a, b) => Number(b.quota_remaining || 0) - Number(a.quota_remaining || 0));
    case "plan_premium_first":
    default:
      return list.sort((a, b) => {
        const planA = String(a.plan_type || "free").toLowerCase() === "premium" ? 1 : 0;
        const planB = String(b.plan_type || "free").toLowerCase() === "premium" ? 1 : 0;
        if (planB !== planA) return planB - planA;
        return String(a.username || "").localeCompare(String(b.username || ""));
      });
  }
};

const renderQuotaModalUserSummary = (record) => {
  if (!record) return null;

  const total = Number(record.quota_total || 0);
  const used = Number(record.quota_used || 0);
  const remaining = Number(record.quota_remaining || 0);
  const usedPercent = total > 0 ? Math.round((used / total) * 100) : 0;
  const tone = getQuotaTone(usedPercent);
  const planLabel = String(record.plan_type || "free").toUpperCase();

  return (
    <div className="portal-quota-modal-summary">
      <div className="portal-quota-modal-summary-main">
        <Text className="portal-quota-modal-summary-label">Account</Text>
        <Text strong className="portal-quota-modal-summary-value">
          {record.username || record.email || record.user_id}
        </Text>
      </div>
      <div className="portal-quota-modal-summary-main">
        <Text className="portal-quota-modal-summary-label">Plan</Text>
        <Tag color={planLabel === "PREMIUM" ? "processing" : "default"}>{planLabel}</Tag>
      </div>
      <div className="portal-quota-modal-summary-main">
        <Text className="portal-quota-modal-summary-label">Usage</Text>
        <Space size={6}>
          <Text className="portal-quota-modal-summary-value">{`${used} / ${total}`}</Text>
          <Tag
            style={{
              backgroundColor: tone.bg,
              color: tone.text,
              borderColor: tone.border,
              fontWeight: 600,
              margin: 0,
            }}
          >
            {`${usedPercent}%`}
          </Tag>
        </Space>
      </div>
      <div className="portal-quota-modal-summary-main">
        <Text className="portal-quota-modal-summary-label">Remaining</Text>
        <Text className="portal-quota-modal-summary-value">{remaining}</Text>
      </div>
    </div>
  );
};

const renderQuotaModalTitle = (heading, record) => (
  <div className="portal-quota-modal-head">
    <div className="portal-quota-modal-head-title">{heading}</div>
    <div className="portal-quota-modal-head-sub">
      {record?.email || record?.username || "User"}
    </div>
  </div>
);

const renderQuotaModalFooter = ({ onCancel, onSubmit, loading, submitLabel }) => (
  <div className="portal-quota-modal-footer">
    <Button type="default" className="portal-btn" onClick={onCancel}>
      Cancel
    </Button>
    <Button type="primary" className="portal-btn" loading={loading} onClick={onSubmit}>
      {submitLabel}
    </Button>
  </div>
);

const AdminCardSortDropdown = ({ value, options, onChange }) => {
  const current = options.find((item) => item.value === value) || options[0];

  return (
    <div className="admin-card-sort-control" onClick={(event) => event.stopPropagation()}>
      <span className="admin-card-sort-label">Sort</span>
      <Dropdown
        menu={{
          items: options.map((item) => ({ key: item.value, label: item.label })),
          selectable: true,
          selectedKeys: [value],
          onClick: ({ key }) => onChange(key),
        }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button type="default" className="portal-btn portal-card-head-btn admin-card-sort-btn">
          <span className="admin-card-sort-btn-label">{current.label}</span>
          <DownOutlined className="admin-card-sort-btn-icon" />
        </Button>
      </Dropdown>
    </div>
  );
};

const shortenLogEndpoint = (endpoint) => {
  const value = String(endpoint || "").trim();
  if (!value) return "-";
  if (value.length <= 40) return value;
  return `${value.slice(0, 28)}…${value.slice(-6)}`;
};

const renderAdminLogStatusTag = (record) => {
  const code = Number(record?.status_code);
  const label = record?.status || (Number.isFinite(code) ? String(code) : "-");
  const normalized = String(label).toUpperCase();

  let color = "default";
  let display = label;
  if (Number.isFinite(code)) {
    if (code >= 500) {
      color = "error";
      display = `${code}`;
    } else if (code >= 400) {
      color = "warning";
      display = `${code}`;
    } else if (code >= 200 && code < 300) {
      color = "success";
      display = `${code}`;
    } else {
      display = `${code}`;
    }
  } else if (normalized.includes("SUCCESS")) {
    color = "success";
    display = "OK";
  } else if (normalized.includes("CLIENT") || normalized.includes("ERROR")) {
    color = "warning";
    display = "Error";
  }

  return (
    <Tag color={color} className="admin-log-status-tag" title={label}>
      {display}
    </Tag>
  );
};

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const BRAND_LOGO_PATH = "/branding/onechain-logo.png";

const normalizePathForTry = (rawPath) => {
  if (!rawPath) return "/";
  const asString = rawPath.trim();
  const withoutHost = asString.replace(/^https?:\/\/[^/]+/i, "");

  if (withoutHost.startsWith("/api/v2")) return withoutHost;
  if (withoutHost.startsWith("/api/")) return withoutHost.replace(/^\/api/, "/api/v2");
  if (withoutHost.startsWith("/")) return `/api/v2${withoutHost}`;

  return `/api/v2/${withoutHost}`;
};

const applyPathParams = (rawPath, bodyValue) => {
  if (!rawPath) return "/";

  const bodyObject = safeParse(bodyValue, {});
  const safeBody = bodyObject && typeof bodyObject === "object" && !Array.isArray(bodyObject) ? bodyObject : {};

  return rawPath.replace(/\{([^}]+)\}/g, (match, paramName) => {
    const value = safeBody[paramName];
    if (value === undefined || value === null || value === "") return match;
    return encodeURIComponent(String(value));
  });
};

const safeParse = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatJson = (value) => {
  if (!value) return "{}";
  if (typeof value === "string") {
    const parsed = safeParse(value, null);
    return parsed ? JSON.stringify(parsed, null, 2) : value;
  }
  return JSON.stringify(value, null, 2);
};

const normalizeJsonForDisplay = (value) => {
  const walk = (input) => {
    if (typeof input === "string") {
      const nested = safeParse(input, null);
      return nested === null ? input : walk(nested);
    }

    if (Array.isArray(input)) {
      return input.map((item) => walk(item));
    }

    if (input && typeof input === "object") {
      return Object.fromEntries(Object.entries(input).map(([key, item]) => [key, walk(item)]));
    }

    return input;
  };

  const normalized = walk(value);
  if (typeof normalized === "string") return normalized;
  return JSON.stringify(normalized ?? {}, null, 2);
};

const isEffectivelyEmptyJson = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed === "{}" || trimmed === "[]";
  }

  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === "object") return Object.keys(value).length === 0;

  return false;
};

const renderJsonBodyBlock = (value, emptyLabel) => {
  if (isEffectivelyEmptyJson(value)) {
    return <Text type="secondary">{emptyLabel}</Text>;
  }

  return (
    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {normalizeJsonForDisplay(value)}
    </pre>
  );
};

const parseAdminLogRequestContext = (log) => {
  const raw = log?.request_context ?? log?.requestContext ?? null;
  const parsed = safeParse(raw, null);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return parsed;
  }
  return {};
};

const getAdminLogPathParams = (log) => {
  const context = parseAdminLogRequestContext(log);
  const pathParams = context?.path_params;
  if (pathParams && typeof pathParams === "object" && !Array.isArray(pathParams) && Object.keys(pathParams).length > 0) {
    return pathParams;
  }
  return null;
};

const renderAdminLogRequestInput = (log) => {
  const method = String(log?.method || "").toUpperCase();
  const pathParams = getAdminLogPathParams(log);
  const requestBody = log?.request_body ?? log?.requestBody ?? null;
  const hasBody = !isEffectivelyEmptyJson(requestBody);
  const isPathParamOnly = ["GET", "DELETE", "HEAD"].includes(method) && !hasBody && pathParams;

  if (isPathParamOnly) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text type="secondary">
          No HTTP body for {method}. Values below were taken from the URL path (e.g. /products/type/watson).
        </Text>
        {renderJsonBodyBlock(pathParams, "No path parameters captured.")}
      </Space>
    );
  }

  if (hasBody) {
    return renderJsonBodyBlock(requestBody, "No request body captured.");
  }

  if (pathParams) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text type="secondary">No request body captured.</Text>
        <Text strong>Path parameters</Text>
        {renderJsonBodyBlock(pathParams, "No path parameters captured.")}
      </Space>
    );
  }

  return renderJsonBodyBlock(requestBody, method === "GET" || method === "DELETE" || method === "HEAD"
    ? "No request body captured (normal for GET/DELETE)."
    : "No request body captured.");
};

const getTryBodyFallback = (endpoint) => {
  const path = String(endpoint?.url || "");
  const paramsSample = safeParse(endpoint?.params, {});
  const fallback = {};

  const placeholderMatches = Array.from(path.matchAll(/\{([^}]+)\}/g)).map((match) => match[1]);
  placeholderMatches.forEach((name) => {
    if (!fallback[name]) fallback[name] = "String";
  });

  const paramKeys = paramsSample && typeof paramsSample === "object" ? Object.keys(paramsSample) : [];
  const querySample = safeParse(endpoint?.query, {});
  const queryKeys = querySample && typeof querySample === "object" ? Object.keys(querySample) : [];
  const bodySample = safeParse(endpoint?.body, {});
  const bodyKeys = bodySample && typeof bodySample === "object" ? Object.keys(bodySample) : [];
  const hasTxid = path.toLowerCase().includes("txid") || paramKeys.some((key) => key.toLowerCase().includes("txid"));
  const hasProductId = path.toLowerCase().includes("productid") || paramKeys.some((key) => key.toLowerCase().includes("productid"));

  if (hasTxid && !fallback.txid) fallback.txid = "String";
  if (hasProductId && !fallback.productId) fallback.productId = "String";

  [...paramKeys, ...queryKeys, ...bodyKeys].forEach((k) => {
    if (!fallback[k]) fallback[k] = "String";
  });

  const nameLower = String(endpoint?.name || "").toLowerCase();
  const descLower = String(endpoint?.description || "").toLowerCase();
  if ((nameLower.includes("txid") || descLower.includes("txid")) && !fallback.txid) fallback.txid = "String";
  if ((nameLower.includes("product") && nameLower.includes("id")) || descLower.includes("product id")) {
    if (!fallback.productId) fallback.productId = "String";
    if (!fallback.id) fallback.id = "String";
  }

  return fallback;
};

const getDocSampleBody = (endpoint) => {
  const parsedBody = safeParse(endpoint?.body, null);
  if (parsedBody && typeof parsedBody === "object" && !Array.isArray(parsedBody) && Object.keys(parsedBody).length > 0) {
    return parsedBody;
  }

  const fallbackBody = getTryBodyFallback(endpoint);
  if (Object.keys(fallbackBody).length > 0) return fallbackBody;

  return {};
};

const buildUsageSeries = (logs, days) => {
  const bucket = {};
  const labels = [];

  for (let i = days - 1; i >= 0; i -= 1) {
    const key = moment().subtract(i, "days").format("YYYY-MM-DD");
    labels.push(moment(key).format("MM/DD"));
    bucket[key] = { req: 0, err: 0, success: 0 };
  }

  logs.forEach((log) => {
    const key = moment(log.createdAt).format("YYYY-MM-DD");
    if (!bucket[key]) return;

    bucket[key].req += 1;
    if (log.status_code >= 400) bucket[key].err += 1;
    if (log.status_code >= 200 && log.status_code < 300) bucket[key].success += 1;
  });

  const keys = Object.keys(bucket).sort();

  return {
    labels,
    requests: keys.map((key) => bucket[key].req),
    errors: keys.map((key) => bucket[key].err),
    success: keys.map((key) => bucket[key].success),
  };
};

const getQuotaTone = (usedPercent) => {
  if (usedPercent >= 80) {
    return {
      label: "High",
      bg: "#FFE9EC",
      text: "#B42318",
      border: "#FFB4BE",
    };
  }
  if (usedPercent >= 50) {
    return {
      label: "Monitor",
      bg: "#FFF4DC",
      text: "#B26B00",
      border: "#FFD590",
    };
  }
  return {
    label: "Safe",
    bg: "#E8F4FF",
    text: "#0B6FA4",
    border: "#B9DFFF",
  };
};

/** Split endpoint name so the suffix (e.g. PRODUCT_ID) stays visible when the row is narrow. */
const splitDocEndpointLabel = (name) => {
  const full = String(name || "").trim();
  if (!full) return { prefix: "", suffix: "-", full };

  const byMarker = "_BY_";
  const byIdx = full.lastIndexOf(byMarker);
  if (byIdx >= 0) {
    return {
      prefix: full.slice(0, byIdx + byMarker.length),
      suffix: full.slice(byIdx + byMarker.length),
      full,
    };
  }

  const parts = full.split("_");
  if (parts.length <= 2) {
    return { prefix: "", suffix: full, full };
  }

  return {
    prefix: `${parts.slice(0, -1).join("_")}_`,
    suffix: parts[parts.length - 1],
    full,
  };
};

const PortalShell = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.common.user);
  const isAdmin = useMemo(() => (currentUser?.roles || []).includes("ROLE_ADMIN"), [currentUser]);

  const [activeKey, setActiveKey] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth < 992 : false)
  );
  const [siderCollapsed, setSiderCollapsed] = useState(isMobileNav);

  const handleSiderBreakpoint = (broken) => {
    setIsMobileNav(broken);
    setSiderCollapsed(broken);
  };

  const handlePortalMenuClick = ({ key }) => {
    setActiveKey(key);
    if (isMobileNav) {
      setSiderCollapsed(true);
    }
  };

  const [allEndpoints, setAllEndpoints] = useState([]);
  const [ownedEndpoints, setOwnedEndpoints] = useState([]);
  const [globalKeys, setGlobalKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [myQuotaStatus, setMyQuotaStatus] = useState(null);

  const [usageWindow, setUsageWindow] = useState(7);

  const [docSearch, setDocSearch] = useState("");
  const [docCategoryFilter, setDocCategoryFilter] = useState("all");
  const [docMethodFilter, setDocMethodFilter] = useState("all");

  const [selectedDocId, setSelectedDocId] = useState(null);
  const [tryMode, setTryMode] = useState("live");
  const [tryMethod, setTryMethod] = useState("GET");
  const [tryPath, setTryPath] = useState("");
  const [tryApiKey, setTryApiKey] = useState("");
  const [tryBody, setTryBody] = useState("{}");
  const [tryResult, setTryResult] = useState(null);
  const [sendingTry, setSendingTry] = useState(false);
  const [syncingDocs, setSyncingDocs] = useState(false);

  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [adminUsageLoading, setAdminUsageLoading] = useState(false);
  const [adminUsageSummary, setAdminUsageSummary] = useState([]);
  const [adminUsageSortKey, setAdminUsageSortKey] = useState("requests_desc");
  const [adminUsageSearch, setAdminUsageSearch] = useState("");
  const [adminHasDefaultView, setAdminHasDefaultView] = useState(false);
  const [adminAlertsOpen, setAdminAlertsOpen] = useState(false);
  const [adminUserDetailOpen, setAdminUserDetailOpen] = useState(false);
  const [adminUserDetailTab, setAdminUserDetailTab] = useState("overview");
  const [adminUserDetail, setAdminUserDetail] = useState(null);
  const [adminUserMonthlyUsage, setAdminUserMonthlyUsage] = useState([]);
  const [adminUserMonthlyLoading, setAdminUserMonthlyLoading] = useState(false);
  const [adminActionTimeline, setAdminActionTimeline] = useState([]);
  const [adminTimelineUserFilter, setAdminTimelineUserFilter] = useState("all");
  const [adminQuotaSortKey, setAdminQuotaSortKey] = useState("plan_premium_first");
  const [adminSelectedUser, setAdminSelectedUser] = useState(null);
  const [adminUserLogs, setAdminUserLogs] = useState([]);
  const [adminRoleUpdatingUserId, setAdminRoleUpdatingUserId] = useState(null);
  const [adminAccountUpdatingUserId, setAdminAccountUpdatingUserId] = useState(null);
  const [adminSelectedLog, setAdminSelectedLog] = useState(null);
  const [adminQuotaLoading, setAdminQuotaLoading] = useState(false);
  const [adminQuotaSummary, setAdminQuotaSummary] = useState([]);
  const [quotaPlanModalOpen, setQuotaPlanModalOpen] = useState(false);
  const [quotaAdjustModalOpen, setQuotaAdjustModalOpen] = useState(false);
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [quotaAdjusting, setQuotaAdjusting] = useState(false);
  const [selectedQuotaUser, setSelectedQuotaUser] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [createUserForm] = Form.useForm();
  const [quotaPlanForm] = Form.useForm();
  const [quotaAdjustForm] = Form.useForm();
  const [creatingUser, setCreatingUser] = useState(false);

  const portalHeaderGreeting = useMemo(() => {
    const name = currentUser?.username || "User";
    let subtitle = "Manage your API keys and monitor usage.";

    if (isAdmin) {
      if (activeKey === "overview") {
        subtitle = "Platform dashboard for all users. Use Admin Usage for logs and quota actions.";
      } else if (activeKey === "admin-usage") {
        subtitle = "User logs, quota management, and account controls.";
      } else if (activeKey === "usage") {
        subtitle = "Your API traffic and quota for this account.";
      } else if (activeKey === "api-keys") {
        subtitle = "Generate and manage API keys for integration.";
      } else if (activeKey === "docs") {
        subtitle = "Endpoint documentation and live testing.";
      } else if (activeKey === "profile") {
        subtitle = "Update profile and password settings.";
      }
    } else if (activeKey === "usage") {
      subtitle = "Recent traffic and status summary from API logs.";
    } else if (activeKey === "docs") {
      subtitle = "Browse products APIs and run test requests.";
    }

    return {
      title: `Welcome back, ${name}!`,
      subtitle,
    };
  }, [currentUser, isAdmin, activeKey]);

  const menuItems = useMemo(() => {
    const baseItems = [
      { key: "overview", icon: <AppstoreOutlined />, label: "Overview" },
      { key: "api-keys", icon: <KeyOutlined />, label: "API Keys" },
      { key: "usage", icon: <LineChartOutlined />, label: "Usage" },
      { key: "docs", icon: <FileTextOutlined />, label: "Documentation" },
    ];
    if (!isAdmin) return baseItems;
    return [...baseItems, { key: "admin-usage", icon: <BarChartOutlined />, label: "Admin Usage" }];
  }, [isAdmin]);

  const headerMenuItems = [
    {
      key: "profile-menu",
      label: "Profile",
      icon: <UserOutlined />,
      onClick: () => {
        setActiveKey("profile");
        setHeaderMenuOpen(false);
      },
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: () => {
        setHeaderMenuOpen(false);
        logout();
      },
      danger: true,
    },
  ];

  const refreshData = async () => {
    if (!currentUser?.id) return;
    setLoading(true);

    try {
      const [allRes, ownedRes, keyRes, logRes, profileRes, billingRes, quotaRes] = await Promise.allSettled([
        ApiServices.getAllEndpoints(),
        ApiServices.getUsersAllEndpoints(currentUser.id),
        ApiKeyServices.getUserApiKeys({ user_id: currentUser.id }),
        ApiLogService.apiLogService(),
        ProfileServices.getMyProfile(),
        axios.get("/api/v1/account/billing-config"),
        axios.get("/api/v1/account/quota-status"),
      ]);

      const endpoints = allRes.status === "fulfilled" && Array.isArray(allRes.value?.data) ? allRes.value.data : [];
      const owned = ownedRes.status === "fulfilled" && Array.isArray(ownedRes.value?.data) ? ownedRes.value.data : [];
      const keys = keyRes.status === "fulfilled" && Array.isArray(keyRes.value?.data?.keys) ? keyRes.value.data.keys : [];
      const apiLogs = logRes.status === "fulfilled" && Array.isArray(logRes.value?.data) ? logRes.value.data : [];
      const profileData = profileRes.status === "fulfilled" ? profileRes.value?.data || null : null;
      const billingData = billingRes.status === "fulfilled" ? billingRes.value?.data || null : null;
      const quotaData = quotaRes.status === "fulfilled" ? quotaRes.value?.data || null : null;

      setAllEndpoints(endpoints);
      setOwnedEndpoints(owned);
      setGlobalKeys(keys);
      setLogs(apiLogs);
      setMyProfile(profileData);
      setBillingConfig(billingData);
      setMyQuotaStatus(quotaData);

      const failedCalls = [allRes, ownedRes, keyRes, logRes, profileRes, billingRes, quotaRes].filter((item) => item.status === "rejected");
      if (failedCalls.length > 0) {
        notification.warning({
          message: "Partial data loaded",
          description: "Some sections failed to load. Please re-login or refresh and try again.",
        });
      }

      if (!selectedDocId && endpoints.length > 0) {
        const first = endpoints[0];
        setSelectedDocId(first.id);
      }
    } catch (_error) {
      notification.error({
        message: "Load Failed",
        description: "Cannot load dashboard data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    if (activeKey !== "profile" || !myProfile) return;
    profileForm.setFieldsValue({
      username: myProfile.username,
      email: myProfile.email,
    });
  }, [activeKey, myProfile, profileForm]);

  useEffect(() => {
    if (tryApiKey || !globalKeys.length) return;
    setTryApiKey(globalKeys[0]?.key || "");
  }, [globalKeys, tryApiKey]);

  const selectedDoc = useMemo(
    () => allEndpoints.find((item) => item.id === selectedDocId) || null,
    [allEndpoints, selectedDocId]
  );

  const logsInUsageWindow = useMemo(() => {
    const cutoff = moment().subtract(usageWindow, "days").startOf("day");
    return logs.filter((item) => item.createdAt && moment(item.createdAt).isSameOrAfter(cutoff));
  }, [logs, usageWindow]);

  const usageSummary = useMemo(() => {
    const total = logsInUsageWindow.length;
    const success = logsInUsageWindow.filter((item) => item.status_code >= 200 && item.status_code < 300).length;
    const fail = logsInUsageWindow.filter((item) => item.status_code >= 400).length;
    return {
      total,
      success,
      fail,
      successRate: total ? Math.round((success / total) * 100) : 0,
    };
  }, [logsInUsageWindow]);

  const usageTable = useMemo(() => {
    return logsInUsageWindow.slice().reverse().slice(0, 20).map((item, index) => ({
      key: `${item.id || index}`,
      method: item.method,
      endpoint: item.endpoint,
      status: item.status_code,
      time: moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    }));
  }, [logsInUsageWindow]);

  const topEndpoints = useMemo(() => {
    const countMap = logsInUsageWindow.reduce((acc, item) => {
      const key = item.endpoint || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([endpoint, count], index) => ({ key: `${index}-${endpoint}`, endpoint, count }));
  }, [logsInUsageWindow]);

  const statusDistribution = useMemo(() => {
    const countMap = logsInUsageWindow.reduce((acc, item) => {
      const key = String(item.status_code || "N/A");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countMap)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([status, count], index) => ({ key: `${index}-${status}`, status, count }));
  }, [logsInUsageWindow]);

  const usageSeries = useMemo(() => buildUsageSeries(logsInUsageWindow, usageWindow), [logsInUsageWindow, usageWindow]);

  const quotaUsagePercent = useMemo(() => {
    const total = Number(myQuotaStatus?.quota_total || 0);
    const used = Number(myQuotaStatus?.quota_used || 0);
    if (total <= 0) return 0;
    return Math.min(Math.round((used / total) * 100), 100);
  }, [myQuotaStatus]);

  const usageChartData = useMemo(() => ({
    labels: usageSeries.labels,
    datasets: buildTrafficLineDatasets({
      total: usageSeries.requests,
      success: usageSeries.success,
      errors: usageSeries.errors,
    }),
  }), [usageSeries]);

  const endpointTable = useMemo(() => {
    const fallbackGlobalKey = globalKeys.find((item) => item?.status !== false)?.key || globalKeys[0]?.key || "";
    const sourceEndpoints = ownedEndpoints.length > 0 ? ownedEndpoints : allEndpoints;

    return sourceEndpoints.map((item, index) => {
      const endpointApiKey = item?.api_key || item?.apiKey || item?.key || "";
      const apiKey = endpointApiKey || fallbackGlobalKey || "-";
      const keySource = endpointApiKey ? "Endpoint" : fallbackGlobalKey ? "Global" : "None";

      return {
        key: `${item.id || index}`,
        name: item.name,
        category: item.category,
        method: item.method,
        apiKey,
        keySource,
        status: item.status,
      };
    });
  }, [ownedEndpoints, allEndpoints, globalKeys]);

  const productsEndpoints = useMemo(() => {
    return allEndpoints.filter((item) => String(item?.category || "").toLowerCase() === "products");
  }, [allEndpoints]);

  const globalKeyTable = useMemo(() => {
    return globalKeys.map((item, index) => ({
      key: item.id || `${index}`,
      value: item.key,
      createdAt: moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    }));
  }, [globalKeys]);

  const docsSourceEndpoints = useMemo(() => {
    if (isAdmin) return allEndpoints;
    return allEndpoints.filter((item) => String(item.category || "").toLowerCase() === "products");
  }, [allEndpoints, isAdmin]);

  const docCategories = useMemo(() => {
    const categories = Array.from(new Set(docsSourceEndpoints.map((item) => item.category || "Others")));
    return categories.sort((a, b) => a.localeCompare(b));
  }, [docsSourceEndpoints]);

  const filteredDocs = useMemo(() => {
    const keyword = docSearch.trim().toLowerCase();
    return docsSourceEndpoints.filter((endpoint) => {
      if (docCategoryFilter !== "all" && (endpoint.category || "Others") !== docCategoryFilter) {
        return false;
      }
      if (docMethodFilter !== "all" && endpoint.method !== docMethodFilter) {
        return false;
      }
      if (!keyword) return true;

      const haystack = `${endpoint.name || ""} ${endpoint.url || ""} ${endpoint.description || ""}`.toLowerCase();
      return haystack.includes(keyword);
    });
  }, [docsSourceEndpoints, docSearch, docCategoryFilter, docMethodFilter]);

  const adminUsersWithLogs = useMemo(
    () => adminUsageSummary.filter((item) => Number(item.total_requests || 0) > 0).length,
    [adminUsageSummary]
  );

  const adminMergedUserRowsAll = useMemo(
    () => mergeAdminUsageWithQuota(adminUsageSummary, adminQuotaSummary),
    [adminUsageSummary, adminQuotaSummary]
  );

  const sortedAdminMergedUserRows = useMemo(
    () => sortAdminMergedUserRows(adminMergedUserRowsAll, adminUsageSortKey),
    [adminMergedUserRowsAll, adminUsageSortKey]
  );

  const sortedAdminUsageSummary = sortedAdminMergedUserRows;

  const filteredAdminMergedUserRows = useMemo(() => {
    const keyword = String(adminUsageSearch || "").trim().toLowerCase();
    if (!keyword) return sortedAdminMergedUserRows;

    return sortedAdminMergedUserRows.filter((item) => {
      const roleLabel = item?.is_disabled ? "disabled" : item?.is_admin ? "admin" : "user";
      const lastApiUse = item?.last_request_at ? moment(item.last_request_at).format("YYYY-MM-DD HH:mm") : "";
      const createdAt = item?.created_at ? moment(item.created_at).format("YYYY-MM-DD") : "";
      const searchText = [
        item?.username,
        item?.email,
        item?.user_id,
        item?.total_requests,
        item?.avg_monthly_success,
        item?.avg_monthly_success_12m,
        item?.plan_type,
        item?.success_rate,
        item?.quota_remaining_percent,
        roleLabel,
        lastApiUse,
        createdAt,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");
      return searchText.includes(keyword);
    });
  }, [sortedAdminMergedUserRows, adminUsageSearch]);

  const adminMergedUserRows = filteredAdminMergedUserRows;

  const adminAlerts = useMemo(() => {
    const alerts = [];
    const highRiskUsers = adminQuotaSummary.filter((item) => {
      const total = Number(item.quota_total || 0);
      if (total <= 0) return false;
      return Number(item.quota_used || 0) / total >= 0.8;
    });
    const exhaustedUsers = adminQuotaSummary.filter(
      (item) => Number(item.quota_total || 0) > 0 && Number(item.quota_remaining || 0) <= 0
    );
    const inactiveHighQuota = adminUsageSummary.filter((item) => Number(item.total_requests || 0) === 0).length;

    if (highRiskUsers.length > 0) {
      alerts.push({ key: "quota-risk", level: "warning", text: `${highRiskUsers.length} users are above 80% quota usage.` });
    }
    if (exhaustedUsers.length > 0) {
      alerts.push({ key: "quota-exhausted", level: "error", text: `${exhaustedUsers.length} users are fully exhausted.` });
    }
    if (inactiveHighQuota > 0) {
      alerts.push({ key: "inactive", level: "default", text: `${inactiveHighQuota} users have no API activity yet.` });
    }
    if (usageSummary.fail > 0) {
      alerts.push({ key: "traffic-errors", level: "warning", text: `${usageSummary.fail} error responses in your current usage window.` });
    }
    return alerts;
  }, [adminQuotaSummary, adminUsageSummary, usageSummary.fail]);

  const selectedAdminUserQuota = useMemo(
    () => adminQuotaSummary.find((row) => String(row.user_id) === String(adminUserDetail?.user_id)) || null,
    [adminQuotaSummary, adminUserDetail]
  );

  const adminUserMonthlyChart = useMemo(() => {
    const months = adminUserMonthlyUsage;
    return {
      labels: months.map((item) => item.month),
      datasets: buildTrafficLineDatasets({
        total: months.map((item) => Number(item.total_requests || 0)),
        success: months.map((item) => Number(item.success_requests || 0)),
        errors: months.map((item) => Number(item.error_requests || 0)),
      }),
    };
  }, [adminUserMonthlyUsage]);

  const timelineUserOptions = useMemo(() => {
    const seen = new Set();
    return adminActionTimeline
      .filter((item) => item?.userId || item?.userLabel)
      .map((item) => ({
        value: String(item.userId || item.userLabel),
        label: item.userLabel || `User ${item.userId}`,
      }))
      .filter((item) => {
        if (seen.has(item.value)) return false;
        seen.add(item.value);
        return true;
      });
  }, [adminActionTimeline]);

  const filteredAdminActionTimeline = useMemo(() => {
    if (adminTimelineUserFilter === "all") return adminActionTimeline;
    return adminActionTimeline.filter((item) => String(item.userId || item.userLabel) === adminTimelineUserFilter);
  }, [adminActionTimeline, adminTimelineUserFilter]);

  useEffect(() => {
    try {
      const rawDefaultView = localStorage.getItem(ADMIN_USAGE_DEFAULT_VIEW_STORAGE_KEY);
      if (rawDefaultView) {
        const parsed = JSON.parse(rawDefaultView);
        if (parsed && typeof parsed === "object") {
          setAdminUsageSearch(parsed.search || "");
          setAdminUsageSortKey(parsed.sort || "requests_desc");
          setAdminHasDefaultView(true);
        }
      }
      const rawTimeline = localStorage.getItem(ADMIN_ACTION_TIMELINE_STORAGE_KEY);
      if (rawTimeline) {
        const parsed = JSON.parse(rawTimeline);
        if (Array.isArray(parsed)) setAdminActionTimeline(parsed);
      }
    } catch (_error) {
      // Ignore storage parse errors and fallback to empty state.
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(ADMIN_ACTION_TIMELINE_STORAGE_KEY, JSON.stringify(adminActionTimeline.slice(0, 50)));
  }, [adminActionTimeline]);

  const addAdminActionTimeline = (title, detail, meta = {}) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title,
      detail,
      userId: meta.userId ? String(meta.userId) : null,
      userLabel: meta.userLabel || null,
      createdAt: new Date().toISOString(),
    };
    setAdminActionTimeline((prev) => [entry, ...prev].slice(0, 50));
  };

  const setDefaultAdminUsageView = () => {
    const snapshot = {
      search: adminUsageSearch,
      sort: adminUsageSortKey,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(ADMIN_USAGE_DEFAULT_VIEW_STORAGE_KEY, JSON.stringify(snapshot));
    setAdminHasDefaultView(true);
    notification.success({ message: "Default view set", description: "Current search and sort are now default." });
  };

  const clearDefaultAdminUsageView = () => {
    localStorage.removeItem(ADMIN_USAGE_DEFAULT_VIEW_STORAGE_KEY);
    setAdminHasDefaultView(false);
    notification.info({ message: "Default view cleared" });
  };

  const sortedAdminQuotaSummary = useMemo(
    () => sortAdminQuotaRows(adminQuotaSummary, adminQuotaSortKey),
    [adminQuotaSummary, adminQuotaSortKey]
  );

  const adminPlatformStats = useMemo(() => {
    if (!isAdmin) return null;

    const totalRequests = adminUsageSummary.reduce(
      (acc, item) => acc + Number(item.total_requests || 0),
      0
    );
    const totalSuccess = adminUsageSummary.reduce(
      (acc, item) => acc + Number(item.success_requests || 0),
      0
    );

    const adminUserIds = new Set(
      adminUsageSummary.filter((item) => item.is_admin).map((item) => String(item.user_id))
    );
    const quotaRows = adminQuotaSummary.filter((item) => !adminUserIds.has(String(item.user_id)));
    const premiumCount = quotaRows.filter((item) => String(item.plan_type || "").toLowerCase() === "premium").length;
    const atRiskCount = quotaRows.filter((item) => {
      const total = Number(item.quota_total || 0);
      if (total <= 0) return false;
      return Number(item.quota_used || 0) / total >= 0.8;
    }).length;
    const exhaustedCount = quotaRows.filter(
      (item) => Number(item.quota_total || 0) > 0 && Number(item.quota_remaining || 0) <= 0
    ).length;

    return {
      totalUsers: adminUsageSummary.length,
      usersWithLogs: adminUsersWithLogs,
      totalRequests,
      totalSuccess,
      totalErrors: Math.max(totalRequests - totalSuccess, 0),
      successRate: totalRequests ? Math.round((totalSuccess / totalRequests) * 100) : 0,
      premiumCount,
      freeCount: quotaRows.length - premiumCount,
      atRiskCount,
      exhaustedCount,
      quotaHealthyCount: Math.max(quotaRows.length - atRiskCount - exhaustedCount, 0),
    };
  }, [isAdmin, adminUsageSummary, adminQuotaSummary, adminUsersWithLogs]);

  const adminTopUsers = useMemo(
    () =>
      [...adminUsageSummary]
        .filter((item) => !item.is_admin)
        .sort((a, b) => Number(b.total_requests || 0) - Number(a.total_requests || 0))
        .slice(0, 8),
    [adminUsageSummary]
  );

  const adminOverviewCharts = useMemo(() => {
    if (!isAdmin || !adminPlatformStats) {
      return { traffic: null, quota: null, plans: null, topUsers: null };
    }

    const stats = adminPlatformStats;
    return {
      traffic: {
        labels: ["Success", "Errors"],
        datasets: [{
          data: [stats.totalSuccess, stats.totalErrors],
          backgroundColor: [BRAND_CHART.primary, BRAND_CHART.danger],
          borderWidth: 0,
        }],
      },
      quota: {
        labels: ["Healthy", "≥80%", "Exhausted"],
        datasets: [{
          data: [stats.quotaHealthyCount, stats.atRiskCount, stats.exhaustedCount],
          backgroundColor: [BRAND_CHART.secondary, "#f0ad4e", BRAND_CHART.danger],
          borderWidth: 0,
        }],
      },
      plans: {
        labels: ["Free", "Premium"],
        datasets: [{
          data: [stats.freeCount, stats.premiumCount],
          backgroundColor: [BRAND_CHART.muted, BRAND_CHART.deep],
          borderWidth: 0,
        }],
      },
      topUsers: {
        labels: adminTopUsers.slice(0, 5).map((item) => {
          const label = item.username || item.email || "User";
          return label.length > 12 ? `${label.slice(0, 12)}…` : label;
        }),
        datasets: [{
          data: adminTopUsers.slice(0, 5).map((item) => Number(item.total_requests || 0)),
          backgroundColor: BRAND_CHART.primary,
          borderRadius: 4,
          barThickness: 12,
        }],
      },
    };
  }, [isAdmin, adminPlatformStats, adminTopUsers]);

  const adminOverviewUserRows = useMemo(
    () => sortedAdminUsageSummary.filter((item) => !item.is_admin),
    [sortedAdminUsageSummary]
  );

  const adminUsageTabStats = useMemo(() => {
    const clientRows = adminUsageSummary.filter((item) => !item.is_admin);
    const totalRequests = clientRows.reduce((acc, item) => acc + Number(item.total_requests || 0), 0);
    const totalSuccess = clientRows.reduce((acc, item) => acc + Number(item.success_requests || 0), 0);
    const activeUsers = clientRows.filter((item) => Number(item.total_requests || 0) > 0).length;

    const adminUserIds = new Set(
      adminUsageSummary.filter((item) => item.is_admin).map((item) => String(item.user_id))
    );
    const quotaRows = adminQuotaSummary.filter((item) => !adminUserIds.has(String(item.user_id)));
    const atRisk = quotaRows.filter((item) => {
      const total = Number(item.quota_total || 0);
      if (total <= 0) return false;
      return Number(item.quota_used || 0) / total >= 0.8;
    }).length;
    const exhausted = quotaRows.filter(
      (item) => Number(item.quota_total || 0) > 0 && Number(item.quota_remaining || 0) <= 0
    ).length;
    const premiumCount = quotaRows.filter(
      (item) => String(item.plan_type || "").toLowerCase() === "premium"
    ).length;

    return {
      clientAccounts: clientRows.length,
      activeUsers,
      totalRequests,
      successRate: totalRequests ? Math.round((totalSuccess / totalRequests) * 100) : 0,
      atRisk,
      exhausted,
      quotaAttention: atRisk + exhausted,
      premiumCount,
    };
  }, [adminUsageSummary, adminQuotaSummary]);

  const selectedAdminUserInfo = useMemo(
    () => adminUsageSummary.find((item) => item.user_id === adminSelectedUser) || null,
    [adminUsageSummary, adminSelectedUser]
  );

  const accountStatusLabel = useMemo(() => {
    const currentPlan = String(myQuotaStatus?.plan_type || billingConfig?.currentPlan || "free").toLowerCase();
    return currentPlan === "free" ? "Free" : "Premium";
  }, [billingConfig, myQuotaStatus]);

  const quotaWarningTag = useMemo(() => {
    const usedPercent = Number(myQuotaStatus?.warning?.used_percent || 0);
    const tone = getQuotaTone(usedPercent);
    return {
      text: `${usedPercent}% (${tone.label})`,
      style: {
        backgroundColor: tone.bg,
        color: tone.text,
        borderColor: tone.border,
        fontWeight: 600,
      },
    };
  }, [myQuotaStatus]);

  const groupedDocs = useMemo(() => {
    return filteredDocs.reduce((acc, endpoint) => {
      const key = endpoint.category || "Others";
      if (!acc[key]) acc[key] = [];
      acc[key].push(endpoint);
      return acc;
    }, {});
  }, [filteredDocs]);

  useEffect(() => {
    if (!selectedDocId && filteredDocs.length > 0) {
      setSelectedDocId(filteredDocs[0].id);
      return;
    }

    if (selectedDocId && !filteredDocs.some((item) => item.id === selectedDocId)) {
      setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].id : null);
    }
  }, [filteredDocs, selectedDocId]);

  useEffect(() => {
    if (!selectedDoc) return;
    setTryMethod(selectedDoc.method || "GET");
    setTryPath(normalizePathForTry(selectedDoc.url || "/"));
    setTryBody(formatJson(getDocSampleBody(selectedDoc)));
    setTryResult(null);
  }, [selectedDoc]);

  const logout = () => {
    AuthService.logout();
    dispatch(setUser(null));
    dispatch(setAuth({ isAuthenticating: false, isAuthenticated: false }));
  };

  const loadAdminUsageSummary = async () => {
    if (!isAdmin) return;
    try {
      setAdminUsageLoading(true);
      const res = await axios.get("/api/v1/admin/usage-summary");
      const users = Array.isArray(res.data?.users) ? res.data.users : [];
      setAdminUsageSummary(users);

      const hasSelectedUser = users.some((item) => item.user_id === adminSelectedUser);
      if (!hasSelectedUser) {
        setAdminSelectedUser(null);
        setAdminUserLogs([]);
      }
    } catch (error) {
      notification.error({
        message: "Load admin usage failed",
        description: error?.response?.data?.message || "Unable to load usage summary.",
      });
    } finally {
      setAdminUsageLoading(false);
    }
  };

  const loadAdminUserLogs = async (userId) => {
    if (!isAdmin || !userId) return;
    try {
      setAdminUsageLoading(true);
      const res = await axios.get(`/api/v1/admin/usage/${userId}?limit=100`);
      const logs = Array.isArray(res.data?.logs) ? res.data.logs : [];
      setAdminUserLogs(logs);
    } catch (error) {
      notification.error({
        message: "Load user logs failed",
        description: error?.response?.data?.message || "Unable to load user logs.",
      });
    } finally {
      setAdminUsageLoading(false);
    }
  };

  const loadAdminUserMonthlyUsage = async (userId) => {
    if (!isAdmin || !userId) return;
    try {
      setAdminUserMonthlyLoading(true);
      const res = await axios.get(`/api/v1/admin/usage/${userId}/monthly?months=12`);
      setAdminUserMonthlyUsage(Array.isArray(res.data?.months) ? res.data.months : []);
    } catch (error) {
      setAdminUserMonthlyUsage([]);
      notification.error({
        message: "Load monthly usage failed",
        description: error?.response?.data?.message || "Unable to load monthly analytics.",
      });
    } finally {
      setAdminUserMonthlyLoading(false);
    }
  };

  const loadAdminQuotaSummary = async () => {
    if (!isAdmin) return;
    try {
      setAdminQuotaLoading(true);
      const res = await axios.get("/api/v1/admin/quota-summary");
      const users = Array.isArray(res.data?.users) ? res.data.users : [];
      setAdminQuotaSummary(users);
    } catch (error) {
      notification.error({
        message: "Load quota summary failed",
        description: error?.response?.data?.message || "Unable to load quota summary.",
      });
    } finally {
      setAdminQuotaLoading(false);
    }
  };

  const openQuotaPlanModal = (record) => {
    if (!record?.user_id) return;
    Modal.confirm({
      title: "Open plan editor?",
      centered: true,
      content: `You're updating ${record.username || record.email || record.user_id}. Current plan: ${String(record.plan_type || "free").toUpperCase()}, total quota: ${Number(record.quota_total || 0)}.`,
      okText: "Continue",
      cancelText: "Cancel",
      onOk: () => {
        setSelectedQuotaUser(record);
        quotaPlanForm.setFieldsValue({
          plan_type: record.plan_type || "free",
          quota_total: Number(record.quota_total || 50),
          quota_period: record.plan_type === "premium" ? (record.quota_period || "yearly") : "monthly",
          quota_reset_policy: record.plan_type === "premium" ? (record.quota_reset_policy || "subscription_year") : "calendar_month",
          subscription_start_at: record.subscription_start_at ? moment(record.subscription_start_at).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        });
        setQuotaPlanModalOpen(true);
      },
    });
  };

  const submitQuotaPlanUpdate = async () => {
    if (!selectedQuotaUser?.user_id) return;
    try {
      const values = await quotaPlanForm.validateFields();
      setQuotaSaving(true);
      const payload = {
        plan_type: values.plan_type,
        quota_total: Number(values.quota_total),
        quota_period: values.plan_type === "premium" ? (values.quota_period || "yearly") : "monthly",
        quota_reset_policy: values.plan_type === "premium" ? (values.quota_reset_policy || "subscription_year") : "calendar_month",
      };
      if (values.plan_type === "premium") {
        payload.subscription_start_at = values.subscription_start_at;
      }

      const res = await axios.put(`/api/v1/admin/quota/${selectedQuotaUser.user_id}/plan`, payload);
      notification.success({
        message: "Plan quota updated",
        description: res?.data?.message || "Plan quota has been updated.",
      });
      addAdminActionTimeline(
        "Plan updated",
        `${selectedQuotaUser?.username || selectedQuotaUser?.email || selectedQuotaUser?.user_id}: ${String(payload.plan_type || "").toUpperCase()} / ${Number(payload.quota_total || 0)}`,
        {
          userId: selectedQuotaUser?.user_id,
          userLabel: selectedQuotaUser?.username || selectedQuotaUser?.email || String(selectedQuotaUser?.user_id || ""),
        }
      );
      setQuotaPlanModalOpen(false);
      await loadAdminQuotaSummary();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: "Update plan quota failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setQuotaSaving(false);
    }
  };

  const openQuotaAdjustModal = (record) => {
    if (!record?.user_id) return;
    Modal.confirm({
      title: "Open quota adjustment?",
      centered: true,
      content: `You're adjusting quota for ${record.username || record.email || record.user_id}. Current remaining: ${Number(record.quota_remaining || 0)}.`,
      okText: "Continue",
      cancelText: "Cancel",
      onOk: () => {
        setSelectedQuotaUser(record);
        quotaAdjustForm.setFieldsValue({
          operation: "add",
          amount: 10,
        });
        setQuotaAdjustModalOpen(true);
      },
    });
  };

  const submitQuotaAdjust = async () => {
    if (!selectedQuotaUser?.user_id) return;
    try {
      const values = await quotaAdjustForm.validateFields();
      setQuotaAdjusting(true);
      const payload = {
        operation: values.operation,
        amount: Number(values.amount),
      };
      const res = await axios.put(`/api/v1/admin/quota/${selectedQuotaUser.user_id}/adjust`, payload);
      notification.success({
        message: "Quota adjusted",
        description: res?.data?.message || "User quota has been adjusted.",
      });
      addAdminActionTimeline(
        "Quota adjusted",
        `${selectedQuotaUser?.username || selectedQuotaUser?.email || selectedQuotaUser?.user_id}: ${values.operation} ${Number(values.amount || 0)}`,
        {
          userId: selectedQuotaUser?.user_id,
          userLabel: selectedQuotaUser?.username || selectedQuotaUser?.email || String(selectedQuotaUser?.user_id || ""),
        }
      );
      setQuotaAdjustModalOpen(false);
      await loadAdminQuotaSummary();
      await loadAdminUsageSummary();
    } catch (error) {
      if (error?.errorFields) return;
      notification.error({
        message: "Adjust quota failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setQuotaAdjusting(false);
    }
  };

  const sendQuotaReminder = async (record, force = false) => {
    if (!record?.user_id) return;
    try {
      setAdminQuotaLoading(true);
      const res = await axios.post(
        `/api/v1/admin/quota/${record.user_id}/send-reminder${force ? "?force=true" : ""}`,
        null,
        { timeout: 60000 }
      );
      notification.success({
        message: "Reminder email sent",
        description: res?.data?.message || "Please check inbox (and spam folder).",
      });
      addAdminActionTimeline(
        "Quota reminder sent",
        `${record?.username || record?.email || record?.user_id}${force ? " (forced)" : ""}`,
        {
          userId: record?.user_id,
          userLabel: record?.username || record?.email || String(record?.user_id || ""),
        }
      );
    } catch (error) {
      notification.error({
        message: "Send reminder failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setAdminQuotaLoading(false);
    }
  };

  const openAdminUserDetail = (record, tab = "overview") => {
    if (!record?.user_id) return;
    setAdminUserDetail(record);
    setAdminUserDetailTab(tab);
    setAdminSelectedUser(record.user_id);
    setAdminSelectedLog(null);
    setAdminUserMonthlyUsage([]);
    setAdminUserDetailOpen(true);
    loadAdminUserMonthlyUsage(record.user_id);
    if (tab === "logs") {
      loadAdminUserLogs(record.user_id);
    }
  };

  const openAdminUserHistory = (userId) => {
    if (!userId) return;
    const record =
      adminMergedUserRows.find((item) => String(item.user_id) === String(userId)) ||
      adminUsageSummary.find((item) => String(item.user_id) === String(userId));
    if (record) {
      openAdminUserDetail(record, "logs");
      return;
    }
    setAdminSelectedUser(userId);
    setAdminSelectedLog(null);
    setAdminUserDetailTab("logs");
    setAdminUserDetailOpen(true);
    loadAdminUserLogs(userId);
  };

  const handleAdminUserDetailTabChange = (tab) => {
    setAdminUserDetailTab(tab);
    if (tab === "logs" && adminUserDetail?.user_id) {
      loadAdminUserLogs(adminUserDetail.user_id);
    }
  };

  const openAdminLogDetail = (record) => {
    if (!record) return;
    setAdminSelectedLog(record);
  };

  const changeAdminRole = async (userId, action) => {
    if (!userId || !["upgrade", "revoke"].includes(action)) return;

    try {
      setAdminRoleUpdatingUserId(String(userId));
      const res = await axios.post(`/api/v1/admin/role/${userId}/${action}`);

      notification.success({
        message: action === "upgrade" ? "Admin role upgraded" : "Admin role revoked",
        description: res?.data?.message || undefined,
      });
      addAdminActionTimeline(
        action === "upgrade" ? "Admin role upgraded" : "Admin role revoked",
        `User ID ${userId}`,
        { userId, userLabel: `User ${userId}` }
      );

      await loadAdminUsageSummary();
    } catch (error) {
      notification.error({
        message: action === "upgrade" ? "Upgrade admin failed" : "Revoke admin failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setAdminRoleUpdatingUserId(null);
    }
  };

  const changeUserAccountStatus = async (userId, action) => {
    if (!userId || !["disable", "enable"].includes(action)) return;

    try {
      setAdminAccountUpdatingUserId(String(userId));
      const res = await axios.post(`/api/v1/admin/account/${userId}/${action}`);

      notification.success({
        message: action === "disable" ? "User account disabled" : "User account enabled",
        description: res?.data?.message || undefined,
      });
      addAdminActionTimeline(
        action === "disable" ? "Account disabled" : "Account enabled",
        `User ID ${userId}`,
        { userId, userLabel: `User ${userId}` }
      );

      await loadAdminUsageSummary();
    } catch (error) {
      notification.error({
        message: action === "disable" ? "Disable user failed" : "Enable user failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setAdminAccountUpdatingUserId(null);
    }
  };

  const confirmUserAccountToggle = (record) => {
    if (!record?.user_id) return;

    const isDisabled = record.is_disabled;
    const action = isDisabled ? "enable" : "disable";
    const title = isDisabled ? "Enable this account?" : "Disable this account?";
    const content = isDisabled
      ? `${record.username || record.email || record.user_id} will be allowed to sign in again.`
      : `${record.username || record.email || record.user_id} and all linked API keys will be blocked immediately.`;

    Modal.confirm({
      title,
      content,
      okText: isDisabled ? "Enable" : "Disable",
      okButtonProps: { danger: !isDisabled },
      cancelText: "Cancel",
      centered: true,
      onOk: () => changeUserAccountStatus(record.user_id, action),
    });
  };

  const confirmAdminRoleChange = (record, action) => {
    if (!record?.user_id || !["upgrade", "revoke"].includes(action)) return;

    const isUpgrade = action === "upgrade";
    const displayName = record.username || record.email || record.user_id;
    const title = isUpgrade ? "Promote to admin?" : "Revoke admin role?";
    const content = isUpgrade
      ? `${displayName} will receive full admin access to the platform.`
      : `${displayName} will lose admin access and return to a regular user.`;

    Modal.confirm({
      title,
      content,
      okText: isUpgrade ? "Make Admin" : "Revoke Admin",
      okButtonProps: { danger: !isUpgrade },
      cancelText: "Cancel",
      centered: true,
      onOk: () => changeAdminRole(record.user_id, action),
    });
  };

  useEffect(() => {
    if (!isAdmin) return;
    if (activeKey !== "admin-usage" && activeKey !== "overview") return;
    loadAdminUsageSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeKey !== "admin-usage" && activeKey !== "overview") return;
    loadAdminQuotaSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, isAdmin]);

  const refreshAdminPlatformStats = () => {
    loadAdminUsageSummary();
    loadAdminQuotaSummary();
  };

  const renderAdminAvgActiveCell = (value, record) => {
    const avg = Number(value || 0);
    const months = Number(record.active_months || 0);
    if (months <= 0) {
      return <div className="admin-table-cell-right">—</div>;
    }

    return (
      <div className="admin-table-cell-right admin-avg-cell">
        <Text
          ellipsis={{
            tooltip: `Average successful API calls per month with usage: ${avg}. ${months} active month(s), ${Number(record.total_success_2xx || 0)} successful calls (all time).`,
          }}
        >
          {avg}
        </Text>
      </div>
    );
  };

  const renderAdminAvg12mCell = (value, record) => {
    const avg = Number(value || 0);
    return (
      <div className="admin-table-cell-right admin-avg-cell">
        <Text
          ellipsis={{
            tooltip: `Average successful API calls over the last 12 calendar months (÷12): ${avg}. Total successful in window: ${Number(record.total_success_2xx_12m || 0)}.`,
          }}
        >
          {avg}
        </Text>
      </div>
    );
  };

  const createGlobalKey = async () => {
    try {
      setLoading(true);
      await ApiKeyServices.generateApiKey(null, { user_id: currentUser.id });
      notification.success({ message: "API key generated." });
      await refreshData();
    } catch (error) {
      notification.error({ message: "Create key failed", description: error?.response?.data?.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const deleteGlobalKey = async (value) => {
    try {
      setLoading(true);
      await ApiKeyServices.deleteApiKey({ api_key: value });
      notification.success({ message: "API key deleted." });
      await refreshData();
    } catch (error) {
      notification.error({ message: "Delete key failed", description: error?.response?.data?.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const sendTryRequest = async () => {
    setSendingTry(true);
    setTryResult(null);

    try {
      const pathWithParams = applyPathParams(tryPath, tryBody);
      const unresolvedPlaceholders = (pathWithParams.match(/\{[^}]+\}/g) || []).length;
      const path = normalizePathForTry(pathWithParams);
      const normalizedApiKey = (tryApiKey || "").replace(/\s+/g, "").trim();

      if (unresolvedPlaceholders > 0) {
        setTryResult({
          ok: false,
          status: 400,
          data: {
            message: "Please fill path placeholders in Body JSON before sending.",
            hint: "Add values for txid/productId, then click Send again.",
          },
        });
        setSendingTry(false);
        return;
      }

      if (tryMode === "live" && !normalizedApiKey) {
        setTryResult({
          ok: false,
          status: 400,
          data: { message: "API key is required in Live mode." },
        });
        setSendingTry(false);
        return;
      }

      if (tryMode === "mock") {
        const mockData = {
          mode: "mock",
          method: tryMethod,
          path,
          endpoint: selectedDoc?.name,
          sample: {
            headers: safeParse(selectedDoc?.headers, {}),
            params: safeParse(selectedDoc?.params, {}),
            query: safeParse(selectedDoc?.query, {}),
            body: getDocSampleBody(selectedDoc),
          },
        };
        setTryResult({ ok: true, status: 200, data: mockData });
        setSendingTry(false);
        return;
      }

      const requestConfig = {
        method: tryMethod || "GET",
        url: path,
        headers: {
          Authorization: `Bearer ${normalizedApiKey}`,
          "Content-Type": "application/json",
        },
      };

      if (!["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase())) {
        try {
          requestConfig.data = tryBody ? JSON.parse(tryBody) : {};
        } catch (_error) {
          setTryResult({
            ok: false,
            status: 400,
            data: { message: "Body must be valid JSON." },
          });
          setSendingTry(false);
          return;
        }
      }

      const response = await axios(requestConfig);
      setTryResult({ ok: true, status: response.status, data: response.data });
    } catch (error) {
      const isNetworkError = !error?.response;
      const statusCode = error?.response?.status || 500;
      const responseData = error?.response?.data;
      const isHtmlPayload = typeof responseData === "string" && /<html[\s>]/i.test(responseData);

      let normalizedErrorData = responseData;
      if (statusCode === 405) {
        normalizedErrorData = {
          message: "Upstream returned 405 Method Not Allowed.",
          hint: "Your API key passed local validation. This is now an upstream path/method contract issue, not a JSON type issue.",
          upstream: normalizePathForTry(tryPath),
          detail: isHtmlPayload ? "Nginx HTML error page was returned by upstream." : responseData,
        };
      }

      setTryResult({
        ok: false,
        status: statusCode,
        data: normalizedErrorData || {
          message: isNetworkError ? "Network Error" : error.message,
          hint: isNetworkError
            ? "Please check server MAIN_SERVER_URL and backend proxy target."
            : undefined,
        },
      });
    } finally {
      setSendingTry(false);
    }
  };

  const syncOnlineEndpointTemplates = async () => {
    try {
      setSyncingDocs(true);
      await ApiServices.seedAllApi();
      notification.success({ message: "Endpoint templates synced successfully." });
      await refreshData();
    } catch (error) {
      notification.error({
        message: "Sync failed",
        description: error?.response?.data?.message || "Unable to sync endpoint templates.",
      });
    } finally {
      setSyncingDocs(false);
    }
  };

  const tryPathNormalized = normalizePathForTry(applyPathParams(tryPath || selectedDoc?.url || "/", tryBody));
  const curlSnippet = [
    `curl -X ${tryMethod} "http://localhost:8081${tryPathNormalized}"`,
    `  -H "Authorization: Bearer ${tryApiKey || "<API_KEY>"}"`,
    `  -H "Content-Type: application/json"`,
    !["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase())
      ? `  -d '${tryBody || "{}"}'`
      : null,
  ]
    .filter(Boolean)
    .join(" \\\n+");

  const axiosSnippet = `axios({\n  method: "${tryMethod}",\n  url: "${tryPathNormalized}",\n  headers: {\n    Authorization: "Bearer ${tryApiKey || "<API_KEY>"}",\n    "Content-Type": "application/json"\n  },\n  data: ${["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase()) ? "undefined" : tryBody || "{}"}\n});`;

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notification.success({ message: `${label} copied` });
    } catch (error) {
      notification.error({ message: `Failed to copy ${label}` });
    }
  };

  const showUpgradeInfo = () => {
    setUpgradeModalOpen(true);
  };

  const openEndpointInDocs = (endpointId) => {
    if (!endpointId) return;
    setSelectedDocId(endpointId);
    setActiveKey("docs");
  };

  const renderApiKeyValue = (value) => {
    if (!value || value === "-") return <Text type="secondary">-</Text>;

    return (
      <div className="api-key-cell">
        <Text className="api-key-value">{value}</Text>
        <Button
          size="small"
          icon={<CopyOutlined />}
          className="copy-key-btn"
          onClick={() => copyText(value, "API key")}
        >
          Copy
        </Button>
      </div>
    );
  };

  const handleProfileSave = async () => {
    try {
      const values = await profileForm.validateFields();
      setProfileSaving(true);

      const res = await ProfileServices.updateMyProfile({
        username: values.username,
        email: myProfile?.email || values.email,
      });
      const updatedUser = res.data?.user;
      if (updatedUser) {
        const old = AuthService.getCurrentUser() || {};
        const merged = { ...old, username: updatedUser.username, email: updatedUser.email };
        localStorage.setItem("user", JSON.stringify(merged));
        dispatch(setUser(merged));
        setMyProfile((prev) => ({ ...prev, ...updatedUser }));
      }

      notification.success({ message: "Profile updated successfully." });
    } catch (error) {
      notification.error({
        message: "Update profile failed",
        description: error?.response?.data?.message || error?.message || "Please try again.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        notification.error({ message: "New password and confirm password do not match." });
        return;
      }

      setPasswordSaving(true);
      await ProfileServices.changeMyPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      passwordForm.resetFields();
      notification.success({ message: "Password updated successfully." });
    } catch (error) {
      notification.error({
        message: "Update password failed",
        description: error?.response?.data?.message || error?.message || "Please try again.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const renderAdminPlatformDashboard = () => {
    const stats = adminPlatformStats || {};
    const activePercent = stats.totalUsers
      ? Math.round((Number(stats.usersWithLogs || 0) / Number(stats.totalUsers || 1)) * 100)
      : 0;

    return (
      <div className="admin-overview-compact">
        <Card
          className="portal-card admin-overview-dashboard"
          loading={adminUsageLoading || adminQuotaLoading}
          title="Platform Overview"
          extra={(
            <Space size={8} wrap className="admin-overview-head-actions">
              <Button
                type="default"
                className="portal-btn portal-card-head-btn admin-overview-head-btn"
                onClick={() => setActiveKey("admin-usage")}
              >
                Quota & logs
              </Button>
              <Button
                type="primary"
                className="portal-btn portal-card-head-btn admin-overview-head-btn"
                onClick={refreshAdminPlatformStats}
                loading={adminUsageLoading || adminQuotaLoading}
              >
                Refresh
              </Button>
            </Space>
          )}
        >
          <Row gutter={[10, 10]} className="admin-kpi-row">
            {[
              { label: "Users", value: stats.totalUsers || 0, sub: `${stats.usersWithLogs || 0} with logs` },
              { label: "Requests", value: stats.totalRequests || 0, sub: `${stats.successRate || 0}% OK` },
              { label: "Premium", value: stats.premiumCount || 0, sub: `${stats.freeCount || 0} free` },
              { label: "Alerts", value: stats.atRiskCount || 0, sub: `${stats.exhaustedCount || 0} exhausted` },
            ].map((kpi) => (
              <Col xs={12} sm={12} md={6} key={kpi.label}>
                <div className="admin-kpi-tile admin-kpi-tile--row">
                  <Text className="admin-kpi-label">{kpi.label}</Text>
                  <Text className="admin-kpi-value">{kpi.value}</Text>
                  <Text className="admin-kpi-sub">{kpi.sub}</Text>
                </div>
              </Col>
            ))}
          </Row>

          <Row gutter={[10, 10]} className="admin-charts-row">
            <Col xs={12} sm={8} md={6}>
              <div className="admin-active-panel">
                <Text className="admin-chart-title">Active users</Text>
                <Text className="admin-active-percent">{activePercent}%</Text>
                <Progress percent={activePercent} showInfo={false} strokeColor={BRAND_CHART.primary} />
                <Text className="admin-kpi-sub">
                  {stats.usersWithLogs || 0} of {stats.totalUsers || 0} accounts
                </Text>
              </div>
            </Col>
            {[
              { title: "Traffic", data: adminOverviewCharts.traffic },
              { title: "Quota health", data: adminOverviewCharts.quota },
              { title: "Plans", data: adminOverviewCharts.plans },
            ].map((chart) => (
              <Col xs={12} sm={8} md={6} key={chart.title}>
                <div className="admin-mini-chart admin-mini-chart--equal">
                  <Text className="admin-chart-title">{chart.title}</Text>
                  <div className="admin-chart-canvas admin-chart-canvas--donut">
                    {chart.data ? (
                      <Doughnut data={chart.data} options={compactDoughnutOptions} />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="—" />
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <div className="admin-bar-chart-wrap admin-bar-chart-wrap--full">
            <Text className="admin-chart-title">Top users by requests</Text>
            <div className="admin-bar-chart-canvas admin-bar-chart-canvas--full">
              {adminTopUsers.length > 0 && adminOverviewCharts.topUsers ? (
                <Bar data={adminOverviewCharts.topUsers} options={compactBarOptions} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity" />
              )}
            </div>
          </div>
        </Card>

        <Card
          className="portal-card"
          style={{ marginTop: 12 }}
          title="All users — usage & quota"
          extra={(
            <Button
              type="primary"
              className="portal-btn portal-card-head-btn"
              onClick={() => setActiveKey("admin-usage")}
            >
              Manage in Admin Usage
            </Button>
          )}
        >
          <Table
            size="small"
            loading={adminUsageLoading || adminQuotaLoading}
            dataSource={adminOverviewUserRows}
            pagination={{ pageSize: 8, size: "small", showSizeChanger: false }}
            scroll={{ x: 920 }}
            className="admin-usage-user-table"
            columns={[
              { title: "User", dataIndex: "username", ellipsis: true, width: 120, align: "left" },
              { title: "Email", dataIndex: "email", ellipsis: true, width: 160, align: "left" },
              { title: "Requests", dataIndex: "total_requests", width: 88, align: "right" },
              {
                title: "Avg active mo",
                dataIndex: "avg_monthly_success",
                width: 108,
                align: "right",
                className: "admin-avg-col-cell",
                onHeaderCell: () => ({ className: "admin-avg-col-header" }),
                render: renderAdminAvgActiveCell,
              },
              {
                title: "Avg 12 mo",
                dataIndex: "avg_monthly_success_12m",
                width: 88,
                align: "right",
                className: "admin-avg-col-cell",
                onHeaderCell: () => ({ className: "admin-avg-col-header" }),
                render: renderAdminAvg12mCell,
              },
              {
                title: "OK%",
                dataIndex: "success_rate",
                width: 64,
                align: "right",
                render: (v) => `${v}%`,
              },
              {
                title: "Plan",
                dataIndex: "plan_type",
                width: 80,
                align: "center",
                render: (v) => (
                  <div className="admin-table-cell-center">
                    <Tag color={String(v).toLowerCase() === "premium" ? "processing" : "default"}>
                      {String(v || "free").toUpperCase()}
                    </Tag>
                  </div>
                ),
              },
              {
                title: "Quota",
                key: "quota",
                width: 140,
                align: "center",
                render: (_, r) => (
                  <div className="admin-table-cell-center">
                    <Progress
                      percent={r.quota_percent}
                      size="small"
                      showInfo={false}
                      strokeColor={r.quota_percent >= 80 ? BRAND_CHART.danger : BRAND_CHART.primary}
                    />
                  </div>
                ),
              },
              {
                title: "Used",
                key: "used",
                width: 72,
                align: "right",
                render: (_, r) => (
                  <div className="admin-table-cell-right">{`${r.quota_used}/${r.quota_total}`}</div>
                ),
              },
            ]}
          />
        </Card>
      </div>
    );
  };

  const renderOverview = () => (
    <>
      {isAdmin && renderAdminPlatformDashboard()}

      {isAdmin && <Title level={4} style={{ color: "#174a60", marginTop: 16, marginBottom: 0 }}>Your account</Title>}

      <Row gutter={[16, 16]} style={{ marginTop: isAdmin ? 12 : 0 }}>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}><Statistic title="Active Keys" value={globalKeys.length} /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}>
            <Statistic
              title={isAdmin ? "Registered Endpoints" : "Products Endpoints"}
              value={isAdmin ? allEndpoints.length : productsEndpoints.length}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}><Statistic title="Requests Logged" value={usageSummary.total} /></Card>
        </Col>
      </Row>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Space direction="vertical" size={6}>
          <Space wrap>
            <Text strong>Quota Status</Text>
            <Tag color={String(myQuotaStatus?.plan_type || "free") === "premium" ? "processing" : "default"}>
              {String(myQuotaStatus?.plan_type || "free").toUpperCase()}
            </Tag>
            <Tag style={quotaWarningTag.style}>{quotaWarningTag.text}</Tag>
          </Space>
          <Text>
            {Number(myQuotaStatus?.quota_used || 0)} / {Number(myQuotaStatus?.quota_total || 0)} used, remaining {Number(myQuotaStatus?.quota_remaining || 0)}
          </Text>
          <Text type="secondary">Cycle: {myQuotaStatus?.cycle_id || "-"}</Text>
          <Text style={{ color: "#45667a" }}>
            {myQuotaStatus?.warning?.message || "Usage is within safe range."}
          </Text>
        </Space>
      </Card>

      {!isAdmin && (
      <Card className="portal-card api-overview-card" style={{ marginTop: 16 }}>
        <div className="api-overview-item">
          <div>
            <Text strong>Step 1: Create Access Key</Text>
            <div className="api-overview-desc">
              Create one global API key for your account. Use Bearer &lt;API_KEY&gt; in Try Endpoint live mode.
            </div>
          </div>
          <Button onClick={() => setActiveKey("api-keys")}>{globalKeys.length > 0 ? "Manage Keys" : "Create Key"}</Button>
        </div>
        <div className="api-overview-item">
          <div>
            <Text strong>Step 2: Select API in Documentation</Text>
            <div className="api-overview-desc">
              Choose endpoint, review request schema, then test with Try Endpoint before integration.
            </div>
          </div>
          <Button onClick={() => setActiveKey("docs")}>Open Docs</Button>
        </div>

        <div className="api-overview-item">
          <div>
            <Text strong>Step 3: Send First Request + Copy Snippets</Text>
            <div className="api-overview-desc">
              Use Try Endpoint in Live mode. When successful, copy cURL/Axios snippets into your app.
            </div>
          </div>
          <Button onClick={() => setActiveKey("docs")}>Try Endpoint</Button>
        </div>

        <div className="api-overview-item">
          <div>
            <Text strong>Step 4: Monitor Health</Text>
            <div className="api-overview-desc">
              Track request count, success rate, status distribution, and top endpoints in Usage Analytics.
            </div>
          </div>
          <Button onClick={() => setActiveKey("usage")}>Open Usage</Button>
        </div>
      </Card>
      )}

      {!isAdmin && (
      <Card className="portal-card" title="Integration Tips" style={{ marginTop: 16 }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Text>1) Use the same API key in your server-side integration.</Text>
          <Text>2) Always test with one known-good sample body first.</Text>
          <Text>3) If you get 4xx/5xx, check Usage logs to confirm path, method, and status.</Text>
        </Space>
      </Card>
      )}

      <Card className="portal-card" title="Products Endpoints" style={{ marginTop: 16 }}>
        <Text style={{ color: "#4f6f7f", display: "block", marginBottom: 12 }}>
          {isAdmin
            ? "Products endpoints are the client-facing priority. Keep these templates accurate."
            : "Start from PRODUCTS endpoints first. This is the fastest path to your first successful call."}
        </Text>
        {productsEndpoints.length === 0 ? (
          <Empty description="No products endpoints found" />
        ) : (
          <Table
            size="small"
            pagination={false}
            dataSource={productsEndpoints.slice(0, 6).map((item, index) => ({
              key: `${item.id || index}`,
              id: item.id,
              name: item.name,
              method: item.method,
              description: item.description,
            }))}
            columns={[
              { title: "Endpoint", dataIndex: "name", key: "name" },
              {
                title: "Method",
                dataIndex: "method",
                key: "method",
                width: 110,
                render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag>,
              },
              { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
              {
                title: "Action",
                key: "action",
                width: 130,
                render: (_, record) => (
                  <Button size="small" onClick={() => openEndpointInDocs(record.id)}>Open in Docs</Button>
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );

  const renderApiKeys = () => (
    <>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
        <div>
          <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>API Keys</Title>
          <Text style={{ color: "#45667a" }}>Generate and manage global API keys.</Text>
        </div>
        <Button type="primary" onClick={createGlobalKey}>Create New Key</Button>
      </Space>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        {globalKeyTable.length === 0 ? (
          <Empty description="No API keys yet" />
        ) : (
          <Table
            dataSource={globalKeyTable}
            pagination={{ pageSize: 6 }}
            scroll={{ x: 860 }}
            columns={[
              { title: "API Key", dataIndex: "value", key: "value", render: (value) => renderApiKeyValue(value) },
              { title: "Created At", dataIndex: "createdAt", key: "createdAt" },
              { title: "Action", key: "action", render: (_, record) => <Button danger onClick={() => deleteGlobalKey(record.value)}>Delete</Button> },
            ]}
          />
        )}
      </Card>

      <Card className="portal-card" title="Endpoint Access Mapping" style={{ marginTop: 16 }}>
        <Table
          pagination={{ pageSize: 6 }}
          dataSource={endpointTable}
          scroll={{ x: 960 }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            { title: "Category", dataIndex: "category", key: "category" },
            {
              title: "Method",
              dataIndex: "method",
              key: "method",
              width: 90,
              render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag>,
            },
            { title: "API Key", dataIndex: "apiKey", key: "apiKey", render: (value) => renderApiKeyValue(value) },
            { title: "Key Source", dataIndex: "keySource", key: "keySource", width: 120, render: (value) => <Tag>{value}</Tag> },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              width: 110,
              render: (value) => <Tag color={value ? "success" : "default"}>{value ? "Active" : "Inactive"}</Tag>,
            },
          ]}
        />
      </Card>
    </>
  );

  const renderUsage = () => (
    <>
      <Space align="start" className="usage-topbar" style={{ justifyContent: "space-between", width: "100%" }}>
        <div className="usage-topbar-title">
          <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Usage Analytics</Title>
          <Text style={{ color: "#45667a" }}>Recent traffic and status summary from API logs.</Text>
        </div>
        <Space wrap className="usage-topbar-actions">
          <Button type={usageWindow === 7 ? "primary" : "default"} onClick={() => setUsageWindow(7)}>7 Days</Button>
          <Button type={usageWindow === 30 ? "primary" : "default"} onClick={() => setUsageWindow(30)}>30 Days</Button>
          <Button type={usageWindow === 90 ? "primary" : "default"} onClick={() => setUsageWindow(90)}>90 Days</Button>
        </Space>
      </Space>

      <Card className="portal-card usage-section-card" style={{ marginTop: 16 }}>
        <div className="usage-section-head">
          <div>
            <Text strong className="usage-section-title">API traffic</Text>
            <Text type="secondary" className="usage-section-sub">
              All logged calls in the last {usageWindow} days (includes errors and non-billable responses).
            </Text>
          </div>
        </div>
        <Row gutter={[16, 16]} className="usage-section-stats">
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Requests" value={usageSummary.total} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="2xx responses" value={usageSummary.success} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Errors (4xx/5xx)" value={usageSummary.fail} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Success rate" value={`${usageSummary.successRate}%`} />
          </Col>
        </Row>
      </Card>

      <Card className="portal-card usage-section-card" style={{ marginTop: 16 }}>
        <div className="usage-section-head">
          <div>
            <Space wrap size={8} align="center">
              <Text strong className="usage-section-title">Billing cycle quota</Text>
              <Tag color={String(myQuotaStatus?.plan_type || "free") === "premium" ? "processing" : "default"}>
                {String(myQuotaStatus?.plan_type || "free").toUpperCase()}
              </Tag>
              {myQuotaStatus?.warning?.used_percent != null ? (
                <Tag style={quotaWarningTag.style}>{quotaWarningTag.text}</Tag>
              ) : null}
            </Space>
            <Text type="secondary" className="usage-section-sub">
              Successful (2xx) calls this cycle count toward your limit — separate from the traffic totals above.
              {myQuotaStatus?.cycle_id ? ` Cycle: ${myQuotaStatus.cycle_id}.` : ""}
            </Text>
          </div>
        </div>
        <Progress
          percent={quotaUsagePercent}
          strokeColor={quotaUsagePercent >= 80 ? "#eb6363" : "#00a4bc"}
          className="usage-quota-progress"
        />
        <Row gutter={[16, 16]} className="usage-section-stats">
          <Col xs={24} sm={8}>
            <Statistic title="Used (billable)" value={Number(myQuotaStatus?.quota_used || 0)} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Remaining" value={Number(myQuotaStatus?.quota_remaining || 0)} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Monthly limit" value={Number(myQuotaStatus?.quota_total || 0)} />
          </Col>
        </Row>
      </Card>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <div style={{ height: 320 }}>
          <Line options={trafficLineChartOptions} data={usageChartData} />
        </div>
      </Card>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Table
          dataSource={usageTable}
          pagination={{ pageSize: 8 }}
          columns={[
            { title: "Method", dataIndex: "method", key: "method", render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag> },
            { title: "Endpoint", dataIndex: "endpoint", key: "endpoint", ellipsis: true },
            { title: "Status", dataIndex: "status", key: "status", render: (value) => <Tag color={value >= 400 ? "red" : "green"}>{value}</Tag> },
            { title: "Time", dataIndex: "time", key: "time" },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12} className="usage-bottom-col">
          <Card className="portal-card usage-equal-card" title="Top Endpoints">
            <Table
              dataSource={topEndpoints}
              pagination={false}
              size="small"
              scroll={{ y: 252 }}
              columns={[
                { title: "Endpoint", dataIndex: "endpoint", key: "endpoint", ellipsis: true },
                { title: "Hits", dataIndex: "count", key: "count", width: 100 },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12} className="usage-bottom-col">
          <Card className="portal-card usage-equal-card" title="Status Distribution">
            <Table
              dataSource={statusDistribution}
              pagination={false}
              size="small"
              scroll={{ y: 252 }}
              columns={[
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 120,
                  render: (value) => <Tag color={Number(value) >= 400 ? "red" : "green"}>{value}</Tag>,
                },
                { title: "Count", dataIndex: "count", key: "count", width: 100 },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderBilling = () => (
    <>
      <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Subscription & Billing</Title>
      <Text style={{ color: "#45667a" }}>Plan cards are now loaded from backend config.</Text>
      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Text style={{ display: "block", marginBottom: 12, color: "#2d5166" }}>
          Current Plan: <b>{String(myQuotaStatus?.plan_type || billingConfig?.currentPlan || "free").toUpperCase()}</b>
          {" | "}
          Usage this cycle: <b>{Number(myQuotaStatus?.quota_used || 0)} / {Number(myQuotaStatus?.quota_total || 0)}</b>
          {" | "}
          Remaining: <b>{Number(myQuotaStatus?.quota_remaining || 0)}</b>
        </Text>
        <Text style={{ display: "block", marginBottom: 12, color: "#45667a" }}>
          50% threshold shows dashboard warning only. Emails are sent at 80/90/95 once per cycle.
        </Text>
        <Row gutter={[16, 16]}>
          {(billingConfig?.plans || [
            { key: "free", name: "Free", priceMonthly: 0, requestsPerMinute: 10, monthlyLimit: 10000 },
            { key: "pro", name: "Pro", priceMonthly: 49, requestsPerMinute: 100, monthlyLimit: 1000000 },
            { key: "enterprise", name: "Enterprise", priceMonthly: 299, requestsPerMinute: -1, monthlyLimit: -1 },
          ]).map((plan) => (
            <Col xs={24} md={8} key={plan.key}>
              <Card className="plan-card">
                <Title level={3}>{plan.name}</Title>
                <Text>${plan.priceMonthly}/month</Text>
                <br />
                <Text>{plan.requestsPerMinute < 0 ? "Unlimited" : `${plan.requestsPerMinute} req/min`}</Text>
                <br />
                <Text>{plan.monthlyLimit < 0 ? "Unlimited monthly" : `${plan.monthlyLimit.toLocaleString()} req/month`}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    </>
  );

  const renderProfile = () => (
    <>
      <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Profile</Title>
      <Text style={{ color: "#45667a" }}>Update your account details and password.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }} className="profile-main-row">
        <Col xs={24} lg={12} className="profile-main-col">
          <Card className="portal-card profile-equal-card" title="Edit Profile">
            <Form form={profileForm} layout="vertical">
              <Form.Item label="Username" name="username" rules={[{ required: true, message: "Please input username" }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please input email" }, { type: "email", message: "Email format is invalid" }]}>
                <Input disabled className="readonly-input" />
              </Form.Item>
              <Button type="primary" onClick={handleProfileSave} loading={profileSaving}>Save Profile</Button>
            </Form>

            <div className="profile-account-status-fill">
              <Space align="center" style={{ width: "100%", justifyContent: "space-between" }} wrap>
                <Space size={10}>
                  <Text strong>Account Status</Text>
                  <Tag color={accountStatusLabel === "Premium" ? "success" : "default"}>{accountStatusLabel}</Tag>
                </Space>
                <Button type="primary" onClick={showUpgradeInfo}>Upgrade to Pro</Button>
              </Space>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12} className="profile-main-col">
          <Card className="portal-card profile-equal-card" title="Change Password">
            <Form form={passwordForm} layout="vertical">
              <Form.Item label="Current Password" name="currentPassword" rules={[{ required: true, message: "Please input current password" }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="New Password" name="newPassword" rules={[{ required: true, message: "Please input new password" }, { min: 6, message: "At least 6 characters" }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="Confirm New Password" name="confirmPassword" rules={[{ required: true, message: "Please confirm new password" }]}>
                <Input.Password />
              </Form.Item>
              <Button type="primary" onClick={handlePasswordSave} loading={passwordSaving}>Update Password</Button>
            </Form>
          </Card>
        </Col>
      </Row>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}><Text strong>Current Username</Text><div>{myProfile?.username || "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Current Email</Text><div>{myProfile?.email || "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Member Since</Text><div>{myProfile?.createdAt ? moment(myProfile.createdAt).format("YYYY-MM-DD") : "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Role(s)</Text><div>{(currentUser?.roles || []).join(", ")}</div></Col>
        </Row>
      </Card>
    </>
  );

  const renderDocs = () => {
    const sample = {
      headers: safeParse(selectedDoc?.headers, {}),
      params: safeParse(selectedDoc?.params, {}),
      query: safeParse(selectedDoc?.query, {}),
      body: getDocSampleBody(selectedDoc),
    };

    return (
      <>
        <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>{isAdmin ? "API Documentation" : "Products API Documentation"}</Title>
        <Text style={{ color: "#45667a" }}>
          {isAdmin
            ? "Browse endpoints from DB, filter quickly, and run live/mock requests."
            : "Products endpoints are prioritized for clients. Pick one endpoint, run Try Endpoint, then copy snippets."}
        </Text>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card className="portal-card">
              <Row gutter={[12, 12]}>
                {isAdmin && (
                  <Col xs={24} md={8}>
                    <Button onClick={syncOnlineEndpointTemplates} loading={syncingDocs} block>
                      Sync Online Endpoint Templates
                    </Button>
                  </Col>
                )}
                <Col xs={24} md={8}>
                  <Input placeholder="Search endpoint name/url/description" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} />
                </Col>
                <Col xs={12} md={4}>
                  <Select
                    value={docCategoryFilter}
                    onChange={setDocCategoryFilter}
                    style={{ width: "100%" }}
                    options={[{ label: "All Categories", value: "all" }, ...docCategories.map((c) => ({ label: c, value: c }))]}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Select
                    value={docMethodFilter}
                    onChange={setDocMethodFilter}
                    style={{ width: "100%" }}
                    options={[
                      { label: "All Methods", value: "all" },
                      { label: "GET", value: "GET" },
                      { label: "POST", value: "POST" },
                      { label: "PUT", value: "PUT" },
                      { label: "DELETE", value: "DELETE" },
                      { label: "PATCH", value: "PATCH" },
                    ]}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card className="portal-card docs-list-card" title="Endpoints by Category">
              {Object.keys(groupedDocs).length === 0 && <Empty description="No endpoints" />}
              {Object.entries(groupedDocs).map(([category, endpoints]) => (
                <div key={category} style={{ marginBottom: 14 }}>
                  <Text className="docs-category">{category}</Text>
                  <div className="docs-list-wrap">
                    {endpoints.map((endpoint) => {
                      const { prefix, suffix, full } = splitDocEndpointLabel(endpoint.name);
                      return (
                        <button
                          type="button"
                          key={endpoint.id}
                          title={full}
                          onClick={() => setSelectedDocId(endpoint.id)}
                          className={`docs-item-btn${selectedDocId === endpoint.id ? " active" : ""}`}
                        >
                          <span className="docs-item-btn-label">
                            {prefix ? <span className="docs-item-btn-prefix">{prefix}</span> : null}
                            <span className="docs-item-btn-suffix">{suffix}</span>
                          </span>
                          <Tag className="docs-item-btn-tag">{endpoint.method}</Tag>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card className="portal-card" title={selectedDoc ? `${selectedDoc.method}: ${selectedDoc.name}` : "Select Endpoint"}>
              {selectedDoc ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Text><b>Category:</b> {selectedDoc.category}</Text>
                  <Text><b>Path:</b> {normalizePathForTry(selectedDoc.url)}</Text>
                  <Text><b>Description:</b> {selectedDoc.description || "No description"}</Text>
                  <Text><b>Version:</b> {selectedDoc.version || "N/A"}</Text>

                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Headers Schema / Sample"><pre>{formatJson(sample.headers)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Params Schema / Sample"><pre>{formatJson(sample.params)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Query Schema / Sample"><pre>{formatJson(sample.query)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Body Schema / Sample"><pre>{formatJson(sample.body)}</pre></Card>
                    </Col>
                  </Row>

                  <Card className="try-card" title="Try Endpoint">
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={6}>
                        <Text>Mode</Text>
                        <Select
                          style={{ width: "100%", marginTop: 4 }}
                          value={tryMode}
                          onChange={setTryMode}
                          options={[{ label: "Live", value: "live" }, { label: "Mock", value: "mock" }]}
                        />
                      </Col>
                      <Col xs={24} md={6}>
                        <Text>Method</Text>
                        <Select
                          style={{ width: "100%", marginTop: 4 }}
                          value={tryMethod}
                          onChange={setTryMethod}
                          options={["GET", "POST", "PUT", "DELETE", "PATCH"].map((item) => ({ label: item, value: item }))}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Text>Path</Text>
                        <Input value={tryPath} onChange={(e) => setTryPath(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Text>API Key (required in Live mode)</Text>
                        <Input.Password value={tryApiKey} onChange={(e) => setTryApiKey(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Text>Body (JSON)</Text>
                        <Input.TextArea rows={8} value={tryBody} onChange={(e) => setTryBody(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Button type="primary" onClick={sendTryRequest} loading={sendingTry}>Send</Button>
                      </Col>
                    </Row>
                  </Card>

                  <Card className="try-card result-card" title={tryResult ? `Result (${tryResult.status})` : "Result"}>
                    <pre>{tryResult ? JSON.stringify(tryResult.data, null, 2) : "No response yet. Click Send to test this endpoint."}</pre>
                  </Card>

                  <Card className="try-card" title="Code Snippets">
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div>
                        <Text strong>cURL</Text>
                        <pre>{curlSnippet}</pre>
                        <Button onClick={() => copyText(curlSnippet, "cURL snippet")}>Copy cURL</Button>
                      </div>
                      <div>
                        <Text strong>Axios</Text>
                        <pre>{axiosSnippet}</pre>
                        <Button onClick={() => copyText(axiosSnippet, "Axios snippet")}>Copy Axios</Button>
                      </div>
                    </Space>
                  </Card>
                </Space>
              ) : (
                <Empty description="No endpoint selected" />
              )}
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderAdminUsage = () => {
    const adminUserLogsTable = (
      <div className="admin-drawer-logs-wrap">
        <Table
          className="admin-drawer-logs-table"
          loading={adminUsageLoading}
          dataSource={adminUserLogs.map((item, index) => ({
            ...item,
            key: item.id || `${index}`,
            time: item.createdAt ? moment(item.createdAt).format("MM-DD HH:mm") : "-",
          }))}
          pagination={{ pageSize: 8, size: "small", simple: true }}
          tableLayout="fixed"
          size="small"
          rowClassName={() => "admin-log-row-clickable"}
          onRow={(record) => ({
            onClick: () => openAdminLogDetail(record),
            style: { cursor: "pointer" },
          })}
          columns={[
            {
              title: "Time",
              dataIndex: "time",
              key: "time",
              width: 88,
              render: (value) => (
                <Text className="admin-log-time-cell" ellipsis={{ tooltip: value }}>
                  {value}
                </Text>
              ),
            },
            {
              title: "Method",
              dataIndex: "method",
              key: "method",
              width: 58,
              render: (value) => (
                <Tag color={value === "GET" ? "green" : "blue"} className="admin-log-method-tag">
                  {value || "-"}
                </Tag>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              width: 62,
              align: "center",
              render: (_, record) => renderAdminLogStatusTag(record),
            },
            {
              title: "API path",
              dataIndex: "endpoint",
              key: "endpoint",
              ellipsis: true,
              render: (value) => (
                <Text className="admin-log-endpoint-cell" ellipsis={{ tooltip: value || "-" }}>
                  {shortenLogEndpoint(value)}
                </Text>
              ),
            },
          ]}
        />
      </div>
    );

    return (
      <>
        <Space align="start" className="admin-topbar" style={{ justifyContent: "space-between", width: "100%" }}>
          <div className="admin-topbar-title">
            <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Admin Usage</Title>
            <Text style={{ color: "#45667a" }}>Monitor per-user API usage without changing existing database records.</Text>
          </div>
          <Space wrap className="admin-topbar-actions">
            <Button onClick={refreshAdminPlatformStats} loading={adminUsageLoading || adminQuotaLoading}>Refresh</Button>
            <Badge count={adminAlerts.length} size="small">
              <Button icon={<BellOutlined />} onClick={() => setAdminAlertsOpen(true)}>
                Alerts
              </Button>
            </Badge>
            {isAdmin && (
              <Button type="primary" onClick={() => { createUserForm.resetFields(); setCreateUserModalOpen(true); }}>
                Create User
              </Button>
            )}
          </Space>
        </Space>

        <SwipeableDrawer
          swipeKey="admin-alerts"
          title="Alert Center"
          open={adminAlertsOpen}
          onClose={() => setAdminAlertsOpen(false)}
          width={420}
        >
          {adminAlerts.length === 0 ? (
            <Empty description="No active alerts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {adminAlerts.map((alert) => (
                <div key={alert.key} className="admin-alert-item">
                  <Tag color={alert.level === "error" ? "error" : alert.level === "warning" ? "warning" : "default"}>
                    {alert.level.toUpperCase()}
                  </Tag>
                  <Text>{alert.text}</Text>
                </div>
              ))}
            </Space>
          )}
        </SwipeableDrawer>

        <Modal
          title="Create User"
          open={createUserModalOpen}
          onCancel={() => setCreateUserModalOpen(false)}
          centered
          okText="Create"
          confirmLoading={creatingUser}
          onOk={async () => {
            try {
              const values = await createUserForm.validateFields();
              setCreatingUser(true);
              const res = await axios.post('/api/v1/admin/account/create', values);
              notification.success({ message: 'User created', description: res?.data?.message || '' });
              addAdminActionTimeline("User created", `${values.username} (${values.email})`, {
                userLabel: values.username || values.email,
              });
              setCreateUserModalOpen(false);
              await loadAdminUsageSummary();
            } catch (error) {
              const msg = error?.response?.data?.message || error?.message || 'Failed to create user.';
              notification.error({ message: 'Create user failed', description: msg });
            } finally {
              setCreatingUser(false);
            }
          }}
        >
          <Form form={createUserForm} layout="vertical">
            <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please input username' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please input email' }, { type: 'email', message: 'Invalid email' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please input password' }, { min: 6, message: 'Password must be at least 6 characters' }]}>
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={8}>
            <Card className="portal-card admin-stat-card">
              <Statistic
                title="Active API users"
                value={adminUsageTabStats.activeUsers}
                suffix={(
                  <Text type="secondary" style={{ fontSize: 14, fontWeight: 400 }}>
                    {" "}/ {adminUsageTabStats.clientAccounts}
                  </Text>
                )}
              />
              <Text type="secondary" className="admin-stat-hint">Client accounts with logged traffic</Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="portal-card admin-stat-card">
              <Statistic
                title="All-time API calls"
                value={adminUsageTabStats.totalRequests}
                formatter={(value) => Number(value || 0).toLocaleString()}
              />
              <Text type="secondary" className="admin-stat-hint">
                {adminUsageTabStats.successRate}% successful responses
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card className="portal-card admin-stat-card">
              <Statistic title="Quota attention" value={adminUsageTabStats.quotaAttention} />
              <Text type="secondary" className="admin-stat-hint">
                {adminUsageTabStats.atRisk} at ≥80% usage · {adminUsageTabStats.exhausted} exhausted
                {adminUsageTabStats.premiumCount > 0 ? ` · ${adminUsageTabStats.premiumCount} premium` : ""}
              </Text>
            </Card>
          </Col>
        </Row>

        <Card
          className="portal-card admin-usage-single-card"
          style={{ marginTop: 16 }}
          title="Users — usage & quota"
          extra={(
            <Space size={8} wrap className="admin-card-extra-actions admin-usage-extra-actions">
              <Button className="portal-btn portal-card-head-btn" onClick={setDefaultAdminUsageView}>
                {adminHasDefaultView ? "Update Default" : "Set Default"}
              </Button>
              {adminHasDefaultView ? (
                <Button className="portal-btn portal-card-head-btn" onClick={clearDefaultAdminUsageView}>
                  Clear Default
                </Button>
              ) : null}
              <Input
                allowClear
                value={adminUsageSearch}
                className="admin-usage-search-input"
                placeholder="Search user, email, plan..."
                onChange={(event) => setAdminUsageSearch(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
              <AdminCardSortDropdown
                value={adminUsageSortKey}
                options={ADMIN_USAGE_SORT_OPTIONS}
                onChange={setAdminUsageSortKey}
              />
            </Space>
          )}
        >
          <Text type="secondary" className="admin-usage-layer-hint">
            Tap a row for details. Avg (active mo) = successful calls ÷ months with usage. Avg (12 mo) = successful calls in last 12 months ÷ 12.
          </Text>
          <Text type="secondary" className="mobile-scroll-hint">Swipe table left/right on mobile</Text>
          <Table
            className="admin-usage-user-table admin-merged-user-table"
            loading={adminUsageLoading || adminQuotaLoading}
            dataSource={adminMergedUserRows}
            pagination={{ pageSize: 8 }}
            tableLayout="fixed"
            scroll={{ x: 1480 }}
            rowClassName={(record) => (
              String(record.user_id) === String(adminSelectedUser) ? "admin-row-selected" : ""
            )}
            onRow={(record) => ({
              onClick: () => openAdminUserDetail(record, "overview"),
              style: { cursor: "pointer" },
            })}
            columns={[
              {
                title: "User",
                dataIndex: "username",
                key: "username",
                width: 140,
                render: (value) => (
                  <Text ellipsis={{ tooltip: value || "-" }}>{value || "-"}</Text>
                ),
              },
              {
                title: "Email",
                dataIndex: "email",
                key: "email",
                width: 180,
                ellipsis: true,
                render: (value) => (
                  <Text ellipsis={{ tooltip: value || "-" }}>{value || "-"}</Text>
                ),
              },
              {
                title: "Plan",
                dataIndex: "plan_type",
                key: "plan_type",
                width: 88,
                align: "center",
                render: (v) => (
                  <Tag color={String(v).toLowerCase() === "premium" ? "processing" : "default"}>
                    {String(v || "free").toUpperCase()}
                  </Tag>
                ),
              },
              {
                title: "Quota",
                key: "quota",
                width: 120,
                align: "center",
                render: (_, record) => (
                  <Progress
                    percent={record.quota_percent}
                    size="small"
                    showInfo={false}
                    strokeColor={record.quota_percent >= 80 ? BRAND_CHART.danger : BRAND_CHART.primary}
                  />
                ),
              },
              {
                title: "Used",
                key: "quota_used",
                width: 88,
                align: "right",
                render: (_, record) => (
                  <Text>{`${record.quota_used}/${record.quota_total}`}</Text>
                ),
              },
              {
                title: "Requests",
                dataIndex: "total_requests",
                key: "total_requests",
                width: 88,
                align: "right",
                render: (value) => <div className="admin-table-cell-right">{value ?? 0}</div>,
              },
              {
                title: "Avg success (active mo)",
                dataIndex: "avg_monthly_success",
                key: "avg_monthly_success",
                width: 148,
                align: "right",
                className: "admin-avg-col-cell",
                onHeaderCell: () => ({ className: "admin-avg-col-header" }),
                render: renderAdminAvgActiveCell,
              },
              {
                title: "Avg success (12 mo)",
                dataIndex: "avg_monthly_success_12m",
                key: "avg_monthly_success_12m",
                width: 132,
                align: "right",
                className: "admin-avg-col-cell",
                onHeaderCell: () => ({ className: "admin-avg-col-header" }),
                render: renderAdminAvg12mCell,
              },
              {
                title: "OK%",
                dataIndex: "success_rate",
                key: "success_rate",
                width: 64,
                align: "right",
                render: (v) => <div className="admin-table-cell-right">{v}%</div>,
              },
              {
                title: "Last use",
                dataIndex: "last_request_at",
                key: "last_request_at",
                width: 108,
                align: "right",
                render: (value) => (
                  <div className="admin-table-cell-right">
                    {value ? moment(value).format("MM-DD HH:mm") : "—"}
                  </div>
                ),
              },
              {
                title: "Status",
                key: "status",
                align: "center",
                width: 100,
                render: (_, record) => (
                  <Tag color={record.is_disabled ? "error" : record.is_admin ? "processing" : "default"}>
                    {record.is_disabled ? "Off" : record.is_admin ? "Admin" : "User"}
                  </Tag>
                ),
              },
              {
                title: "Manage",
                key: "manage",
                align: "center",
                width: 132,
                className: "admin-manage-col-cell",
                onHeaderCell: () => ({ className: "admin-manage-col-header" }),
                render: (_, record) => (
                  <Space
                    direction="vertical"
                    size={6}
                    className="admin-manage-actions"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {/* <Button
                      size="small"
                      type="default"
                      block
                      className="admin-manage-btn"
                      onClick={() => openAdminUserDetail(record, "overview")}
                    >
                      Open
                    </Button> */}
                    {!record.is_admin ? (
                      <Button
                        size="small"
                        type="default"
                        block
                        className="admin-manage-btn"
                        disabled={record.is_disabled}
                        loading={String(adminRoleUpdatingUserId) === String(record.user_id)}
                        onClick={() => confirmAdminRoleChange(record, "upgrade")}
                      >
                        Grant Admin
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        type="default"
                        danger
                        block
                        className="admin-manage-btn"
                        disabled={String(record.user_id) === String(currentUser?.id)}
                        loading={String(adminRoleUpdatingUserId) === String(record.user_id)}
                        onClick={() => confirmAdminRoleChange(record, "revoke")}
                      >
                        Revoke Admin
                      </Button>
                    )}
                    <Button
                      size="small"
                      type="default"
                      block
                      className="admin-manage-btn admin-manage-btn--block"
                      icon={<StopOutlined />}
                      danger={!record.is_disabled}
                      disabled={String(record.user_id) === String(currentUser?.id)}
                      loading={String(adminAccountUpdatingUserId) === String(record.user_id)}
                      onClick={() => confirmUserAccountToggle(record)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card
          className="portal-card admin-usage-single-card"
          style={{ marginTop: 16 }}
          title="Admin Action Timeline"
          extra={(
            <Space size={8} wrap className="admin-card-extra-actions">
              <Select
                value={adminTimelineUserFilter}
                className="admin-timeline-filter-select"
                onChange={setAdminTimelineUserFilter}
                options={[
                  { value: "all", label: "All users" },
                  ...timelineUserOptions,
                ]}
              />
              <Button className="portal-btn portal-card-head-btn" onClick={() => setAdminTimelineUserFilter("all")}>
                Clear
              </Button>
            </Space>
          )}
        >
          {filteredAdminActionTimeline.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No actions recorded yet" />
          ) : (
            <Timeline
              items={filteredAdminActionTimeline.slice(0, 10).map((item) => ({
                children: (
                  <div>
                    <Text strong>{item.title}</Text>
                    <div><Text type="secondary">{item.detail}</Text></div>
                    <Text type="secondary">{moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Text>
                  </div>
                ),
              }))}
            />
          )}
        </Card>

        <SwipeableDrawer
          swipeKey="admin-user-detail"
          className="admin-user-detail-drawer"
          title={adminUserDetail ? `${adminUserDetail.username || adminUserDetail.email}` : "User details"}
          open={adminUserDetailOpen}
          onClose={() => {
            setAdminUserDetailOpen(false);
            setAdminUserDetailTab("overview");
          }}
          width={560}
          destroyOnClose={false}
        >
          {adminUserDetail ? (
            <Tabs
              activeKey={adminUserDetailTab}
              onChange={handleAdminUserDetailTabChange}
              className="admin-user-detail-tabs"
              items={[
                {
                  key: "overview",
                  label: "Overview",
                  children: (
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <Card size="small" title="Profile">
                        <div><Text strong>Email:</Text> {adminUserDetail.email || "-"}</div>
                        <div><Text strong>Role:</Text> {adminUserDetail.is_admin ? "Admin" : "User"}</div>
                        <div><Text strong>Status:</Text> {adminUserDetail.is_disabled ? "Disabled" : "Active"}</div>
                      </Card>
                      <Card size="small" title="Usage summary (layer 2 detail)">
                        <div><Text strong>Total requests:</Text> {Number(adminUserDetail.total_requests || 0)}</div>
                        <div>
                          <Text strong>Avg success (active months):</Text>{" "}
                          {Number(adminUserDetail.active_months || 0) > 0
                            ? `${Number(adminUserDetail.avg_monthly_success || 0)} (${adminUserDetail.active_months} mo with usage)`
                            : "—"}
                        </div>
                        <div>
                          <Text strong>Avg success (last 12 mo):</Text>{" "}
                          {Number(adminUserDetail.avg_monthly_success_12m || 0)} (total successful: {Number(adminUserDetail.total_success_2xx_12m || 0)})
                        </div>
                        <div><Text strong>Success rate:</Text> {Number(adminUserDetail.success_rate || 0)}%</div>
                        <div><Text strong>Last API use:</Text> {adminUserDetail.last_request_at ? moment(adminUserDetail.last_request_at).format("YYYY-MM-DD HH:mm:ss") : "—"}</div>
                      </Card>
                      <Card size="small" title="Monthly traffic (last 12 months)" loading={adminUserMonthlyLoading}>
                        {adminUserMonthlyUsage.length === 0 && !adminUserMonthlyLoading ? (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No monthly activity yet" />
                        ) : (
                          <>
                            <div className="admin-monthly-chart-wrap">
                              <Line options={trafficLineChartOptions} data={adminUserMonthlyChart} />
                            </div>
                            <Table
                              className="admin-monthly-usage-table"
                              size="small"
                              pagination={false}
                              dataSource={adminUserMonthlyUsage.map((item) => ({ ...item, key: item.month }))}
                              columns={[
                                { title: "Month", dataIndex: "month", key: "month", width: 88 },
                                { title: "Calls", dataIndex: "total_requests", key: "total_requests", align: "right", width: 64 },
                                { title: "Success", dataIndex: "success_requests", key: "success_requests", align: "right", width: 72 },
                                { title: "Errors", dataIndex: "error_requests", key: "error_requests", align: "right", width: 64 },
                              ]}
                            />
                          </>
                        )}
                      </Card>
                    </Space>
                  ),
                },
                {
                  key: "logs",
                  label: "Logs",
                  children: (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Text type="secondary">Latest API calls for this user. Tap a row for full detail.</Text>
                      {adminUserLogsTable}
                    </Space>
                  ),
                },
                {
                  key: "quota",
                  label: "Quota",
                  children: selectedAdminUserQuota ? (
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <Card size="small" title="Billing">
                        <div><Text strong>Plan:</Text> {String(selectedAdminUserQuota.plan_type || "free").toUpperCase()}</div>
                        <div><Text strong>Used:</Text> {Number(selectedAdminUserQuota.quota_used || 0)} / {Number(selectedAdminUserQuota.quota_total || 0)}</div>
                        <div><Text strong>Remaining:</Text> {Number(selectedAdminUserQuota.quota_remaining || 0)}</div>
                        <div><Text strong>Cycle:</Text> {selectedAdminUserQuota.cycle_id || "—"}</div>
                      </Card>
                      <Space wrap className="admin-quota-action-btns">
                        <Button
                          type="default"
                          className="portal-btn portal-btn-brand-soft"
                          onClick={() => openQuotaPlanModal(selectedAdminUserQuota)}
                        >
                          Update plan
                        </Button>
                        <Button
                          type="default"
                          className="portal-btn portal-btn-brand-soft"
                          onClick={() => openQuotaAdjustModal(selectedAdminUserQuota)}
                        >
                          Adjust quota
                        </Button>
                        <Button
                          type="default"
                          className="portal-btn portal-btn-alert"
                          onClick={() => sendQuotaReminder(selectedAdminUserQuota, true)}
                          loading={adminQuotaLoading}
                        >
                          Send reminder
                        </Button>
                      </Space>
                    </Space>
                  ) : (
                    <Empty description="No quota record for this user" />
                  ),
                },
              ]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No user selected" />
          )}
        </SwipeableDrawer>

        <Modal
          className="portal-quota-modal"
          title={renderQuotaModalTitle("Update plan", selectedQuotaUser)}
          open={quotaPlanModalOpen}
          centered
          width={520}
          onCancel={() => setQuotaPlanModalOpen(false)}
          footer={renderQuotaModalFooter({
            onCancel: () => setQuotaPlanModalOpen(false),
            onSubmit: submitQuotaPlanUpdate,
            loading: quotaSaving,
            submitLabel: "Save plan",
          })}
          destroyOnClose
        >
          {renderQuotaModalUserSummary(selectedQuotaUser)}
          <Form form={quotaPlanForm} layout="vertical" className="portal-quota-modal-form">
            <Form.Item name="plan_type" label="Plan" rules={[{ required: true, message: "Please select a plan." }]}>
              <Select
                options={[
                  { label: "Free", value: "free" },
                  { label: "Premium", value: "premium" },
                ]}
              />
            </Form.Item>
            <Form.Item name="quota_total" label="Quota Total" rules={[{ required: true, message: "Please input quota total." }]}>
              <Input type="number" min={1} />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) =>
                getFieldValue("plan_type") === "premium" ? (
                  <>
                    <Form.Item name="quota_period" label="Quota Period" rules={[{ required: true, message: "Please select period." }]}>
                      <Select options={[{ label: "Yearly", value: "yearly" }]} />
                    </Form.Item>
                    <Form.Item name="quota_reset_policy" label="Reset Policy" rules={[{ required: true, message: "Please select reset policy." }]}>
                      <Select options={[{ label: "Subscription Year", value: "subscription_year" }]} />
                    </Form.Item>
                    <Form.Item
                      name="subscription_start_at"
                      label="Subscription Start Date"
                      rules={[{ required: true, message: "Please input subscription start date." }]}
                    >
                      <Input type="date" />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          className="portal-quota-modal"
          title={renderQuotaModalTitle("Adjust quota", selectedQuotaUser)}
          open={quotaAdjustModalOpen}
          centered
          width={480}
          onCancel={() => setQuotaAdjustModalOpen(false)}
          footer={renderQuotaModalFooter({
            onCancel: () => setQuotaAdjustModalOpen(false),
            onSubmit: submitQuotaAdjust,
            loading: quotaAdjusting,
            submitLabel: "Apply adjustment",
          })}
          destroyOnClose
        >
          {renderQuotaModalUserSummary(selectedQuotaUser)}
          <Text type="secondary" className="portal-quota-modal-hint">
            Add or remove quota for the current billing cycle without changing the plan.
          </Text>
          <Form form={quotaAdjustForm} layout="vertical" className="portal-quota-modal-form">
            <Form.Item name="operation" label="Operation" rules={[{ required: true, message: "Please select operation." }]}>
              <Select
                options={[
                  { label: "Add", value: "add" },
                  { label: "Minus", value: "minus" },
                ]}
              />
            </Form.Item>
            <Form.Item name="amount" label="Amount" rules={[{ required: true, message: "Please input amount." }]}>
              <Input type="number" min={1} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="API Log Detail"
          open={Boolean(adminSelectedLog)}
          onCancel={() => setAdminSelectedLog(null)}
          footer={null}
          width={860}
          destroyOnHidden
        >
          {adminSelectedLog ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Row gutter={[12, 12]}>
                <Col xs={24} md={6}><Text strong>Method</Text><div>{adminSelectedLog.method || "-"}</div></Col>
                <Col xs={24} md={10}><Text strong>Endpoint</Text><div>{adminSelectedLog.endpoint || "-"}</div></Col>
                <Col xs={24} md={4}><Text strong>Status</Text><div>{adminSelectedLog.status_code || "-"}</div></Col>
                <Col xs={24} md={4}><Text strong>Time</Text><div>{adminSelectedLog.createdAt ? moment(adminSelectedLog.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</div></Col>
              </Row>

              <Card size="small" title="Message">
                <Text>{adminSelectedLog.message || "-"}</Text>
              </Card>

              {Number(adminSelectedLog.status_code) === 304 && isEffectivelyEmptyJson(adminSelectedLog.response_body || adminSelectedLog.responseBody) ? (
                <div style={{ padding: "12px 14px", border: "1px solid #cfe7ef", borderRadius: 8, background: "#f7fbfd", color: "#406273" }}>
                  304 Not Modified responses do not include a response body. Check the endpoint headers or upstream cache behavior instead.
                </div>
              ) : null}

              <Card
                size="small"
                title={
                  ["GET", "DELETE", "HEAD"].includes(String(adminSelectedLog.method || "").toUpperCase())
                    ? "Request Input"
                    : "Request Body JSON"
                }
              >
                {renderAdminLogRequestInput(adminSelectedLog)}
              </Card>

              <Card size="small" title="Request Context">
                {renderJsonBodyBlock(adminSelectedLog.request_context ?? adminSelectedLog.requestContext ?? null, "No request context captured.")}
              </Card>

              <Card size="small" title="Response Body JSON">
                {renderJsonBodyBlock(
                  adminSelectedLog.response_body ?? adminSelectedLog.responseBody ?? null,
                  Number(adminSelectedLog.status_code) === 304 ? "No response body captured for 304 Not Modified." : "No response body captured."
                )}
              </Card>

              <Card size="small" title="Response Headers">
                {renderJsonBodyBlock(adminSelectedLog.response_headers ?? adminSelectedLog.responseHeaders ?? null, "No response headers captured.")}
              </Card>
            </Space>
          ) : null}
        </Modal>
      </>
    );
  };

  const renderActive = () => {
    if (activeKey === "overview") return renderOverview();
    if (activeKey === "api-keys") return renderApiKeys();
    if (activeKey === "usage") return renderUsage();
    if (activeKey === "billing") return renderBilling();
    if (activeKey === "profile") return renderProfile();
    if (activeKey === "admin-usage") return renderAdminUsage();
    return renderDocs();
  };

  const headerUserDropdown = (
    <Dropdown
      menu={{ items: headerMenuItems }}
      open={headerMenuOpen}
      onOpenChange={setHeaderMenuOpen}
      placement="bottomRight"
      trigger={["click"]}
      destroyPopupOnHide
    >
      <div className="header-user-block header-user-clickable">
        <div className="header-user-name">{myProfile?.email || currentUser?.email || "-"}</div>
        <div className="header-user-email">{currentUser?.username || "Account"}</div>
      </div>
    </Dropdown>
  );

  return (
    <Layout className="portal-layout">
      <Header className="portal-header">
        <div className="portal-header-sider">
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="mobile-menu-btn"
            onClick={() => setSiderCollapsed((c) => !c)}
            aria-label="Toggle menu"
          />
          <img src={BRAND_LOGO_PATH} alt="OneChain" className="header-brand-logo" />
          {isMobileNav ? (
            <>
              <div className="portal-header-sider-spacer" aria-hidden="true" />
              <div className="header-user-mobile">{headerUserDropdown}</div>
            </>
          ) : null}
        </div>
        <div className="portal-header-main">
          <div className="header-welcome-wrap">
            <div className="header-welcome-title">{portalHeaderGreeting.title}</div>
            <div className="header-welcome-sub">{portalHeaderGreeting.subtitle}</div>
          </div>
          {!isMobileNav ? <div className="header-user-desktop">{headerUserDropdown}</div> : null}
        </div>
      </Header>
      <Layout className="portal-main-layout">
        <Sider
          width={230}
          className="portal-sider"
          breakpoint="lg"
          collapsedWidth={0}
          collapsed={isMobileNav && siderCollapsed}
          onBreakpoint={handleSiderBreakpoint}
          trigger={null}
          theme="dark"
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={handlePortalMenuClick}
            className="portal-menu"
          />
        </Sider>
        {isMobileNav ? (
          <div
            className={`mobile-sider-backdrop${siderCollapsed ? " hidden" : ""}`}
            onClick={() => setSiderCollapsed(true)}
          />
        ) : null}
        <Layout>
          <Content className="portal-content">
            <div key={activeKey} className="animated-tab-content">
              {renderActive()}
            </div>
          </Content>
        </Layout>
      </Layout>
      <Modal
        title="Upgrade to Pro"
        open={upgradeModalOpen}
        onCancel={() => setUpgradeModalOpen(false)}
        onOk={() => setUpgradeModalOpen(false)}
        okText="Understood"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p>Contact the admin for detail.</p>
      </Modal>
    </Layout>
  );
};

export default PortalShell;
